import { formatDisplayDate, formatDisplayDateTime } from "src/utils/dateTime";
import { countryName, displayField, englishText, isRussian } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { VALERA_AUTH } from "src/services/valeraAuth";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Fragment, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Typo } from "@ui/text/Typo";
import { GD } from "src/GD";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { Error as ErrorBlock } from "@ui/error/Error";
import { FileSelect } from "@ui/fileSelect/FileSelect";
import Spacer from "@ui/spacer/Spacer";
import { CountriesData } from "@ui/utils/CountriesData";
import { CardBrandMark, TCardBrand } from "../conponents/icons/CardBrandMark";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { cents } from "src/tools/tools";
import {
	AlertCircle,
	CalendarDays,
	Check,
	ChevronLeft,
	ChevronRight,
	CircleCheck,
	Clipboard,
	ExternalLink,
	FileJson,
	FileUp,
	Download,
	FileWarning,
	List,
	Plus,
	RefreshCw,
	WandSparkles,
	X,
} from "lucide-react";
import { getNotificationErrorMessage, notify } from "src/tools/notify";
import { S_DECTA_SESSION_POPUP } from "../conponents/popup/DectaSessionPopup";
import { DatePickerField, toUTCISODate } from "../conponents/DateRangePicker";
import { HoverTooltip } from "../conponents/HoverTooltip";
import { loadDectaFile } from "src/utils/dectaFile";

const CARD_SX = {
	backgroundColor: "#fff",
	border: "1px solid #d0d5dd",
	borderRadius: "12px",
	boxShadow: "0 1px 2px rgba(16,24,40,.05)",
};

const localeTag = () => (isRussian() ? "ru-RU" : "en-US");
const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

// Monday-first week: 2024-01-01 is a Monday.
const getWeekdays = () =>
	Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2024, 0, 1 + i)).toLocaleDateString(localeTag(), { weekday: "short", timeZone: "UTC" }));

const monthLabel = (year: number, month: number) =>
	capitalize(new Date(Date.UTC(year, month, 1)).toLocaleDateString(localeTag(), { month: "long", timeZone: "UTC" }));

type TMonthKey = { year: number; month: number };
type TDaySelection = TMonthKey & { day: number };
type TMonthData = { entries?: IChargebackDayEntry[]; failed: boolean };
// "open" — today without a chargeback file yet: the day is not closed, so it is not a gap.
type TDayStatus = "future" | "loading" | "failed" | "open" | "missing" | "clean" | "chargebacks";
type TDayInfo = { status: TDayStatus; entry?: IChargebackDayEntry; amount: number };
type TMonthSummary = { count: number; amount: number; missingDays: number; elapsedDays: number };

const shiftMonth = ({ year, month }: TMonthKey, delta: number): TMonthKey => {
	const date = new Date(Date.UTC(year, month + delta, 1));
	return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
};

const isSameMonth = (a: TMonthKey, b: TMonthKey) => a.year === b.year && a.month === b.month;
const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
const utcDayStamp = (date: Date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const sumAmount = (entry?: IChargebackDayEntry) => entry?.chargebacks.reduce((total, chargeback) => total + chargeback.amount, 0) ?? 0;

const getDayInfo = (year: number, month: number, day: number, data: TMonthData, today: Date): TDayInfo => {
	const stamp = Date.UTC(year, month, day);
	const todayStamp = utcDayStamp(today);
	if (stamp > todayStamp) return { status: "future", amount: 0 };
	if (data.failed) return { status: "failed", amount: 0 };
	if (!data.entries) return { status: "loading", amount: 0 };
	const entry = data.entries.find(item => parseInt(item.day, 10) === day);
	if (!entry) return { status: stamp === todayStamp ? "open" : "missing", amount: 0 };
	return entry.count > 0 ? { status: "chargebacks", entry, amount: sumAmount(entry) } : { status: "clean", entry, amount: 0 };
};

const summarizeMonth = (year: number, month: number, data: TMonthData, today: Date, dayLimit?: number): TMonthSummary | undefined => {
	if (!data.entries) return undefined;
	const summary: TMonthSummary = { count: 0, amount: 0, missingDays: 0, elapsedDays: 0 };
	const lastDay = Math.min(daysInMonth(year, month), dayLimit ?? 31);
	for (let day = 1; day <= lastDay; day++) {
		const info = getDayInfo(year, month, day, data, today);
		if (info.status === "future") break;
		summary.elapsedDays++;
		if (info.status === "missing") summary.missingDays++;
		if (info.status === "chargebacks" && info.entry) {
			summary.count += info.entry.count;
			summary.amount += info.amount;
		}
	}
	return summary;
};

// Loaded months and files are reused for 5 minutes, so flipping back and forth does not refetch them.
const CACHE_TTL_MS = 5 * 60 * 1000;
type TCacheEntry<T> = { value: T; at: number };
const monthCache = new Map<string, TCacheEntry<IChargebackDayEntry[]>>();
const fileCache = new Map<string, TCacheEntry<string>>();

const readCache = <T,>(cache: Map<string, TCacheEntry<T>>, key: string): T | undefined => {
	const hit = cache.get(key);
	if (!hit) return undefined;
	if (Date.now() - hit.at < CACHE_TTL_MS) return hit.value;
	cache.delete(key);
	return undefined;
};

const useChargebackMonth = (year: number, month: number, reloadKey: number): TMonthData => {
	const key = `${year}-${month}`;
	const [state, setState] = useState<{ key: string; data: TMonthData } | undefined>(undefined);

	useEffect(() => {
		const cached = readCache(monthCache, key);
		if (cached) {
			setState({ key, data: { entries: cached, failed: false } });
			return;
		}

		let active = true;
		// async + try: REQ_API.request throws synchronously when no worker is registered (e.g. right after a hot reload),
		// and a bare .catch() would not see that throw.
		const load = async () => {
			try {
				const response = await GD.REQ_API.request({ data: { month: month + 1, year }, method: "processing/statementchargeback" });
				if (response.error) {
					if (active) setState({ key, data: { failed: true } });
					return;
				}
				const entries: IChargebackDayEntry[] = response.data ?? [];
				monthCache.set(key, { value: entries, at: Date.now() });
				if (active) setState({ key, data: { entries, failed: false } });
			} catch {
				if (active) setState({ key, data: { failed: true } });
			}
		};

		load();
		return () => {
			active = false;
		};
	}, [key, reloadKey]);

	// Read the cache during render too, so a cached month shows at once instead of flashing the skeleton.
	const cached = readCache(monthCache, key);
	if (cached) return { entries: cached, failed: false };
	return state?.key === key ? state.data : { failed: false };
};

const COLORS = {
	title: "#182230",
	text: "#344054",
	secondary: "#667085",
	muted: "#98a2b3",
	border: "#eaecf0",
	red: "#d92d20",
	green: "#079455",
	amber: "#f79009",
	selected: "#182230",
	today: "#1570ef",
};

const getChargebackTint = (count: number) => (count >= 4 ? "#fecdca" : count >= 2 ? "#fee4e2" : "#fef3f2");

const shimmer = keyframes`
	from { background-position: 100% 0; }
	to { background-position: -100% 0; }
`;

/** Loading placeholder: a soft moving highlight in place of the content it stands for. */
export const Shimmer = styled.div<{ $width?: string; $height?: string; $radius?: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${props => props.$width ?? "100%"};
	max-width: 100%;
	height: ${props => props.$height ?? "12px"};
	border-radius: ${props => props.$radius ?? "6px"};
	background: linear-gradient(90deg, #f2f4f7 30%, #e4e7ec 50%, #f2f4f7 70%);
	background-size: 300% 100%;
	animation: ${shimmer} 1.6s ease-in-out infinite;
	color: #c4c9d2;
	font-size: 13px;
	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const DayCell = ({ day, info, isToday, isSelected, onSelect }: { day: number; info: TDayInfo; isToday: boolean; isSelected: boolean; onSelect: () => void }) => {
	if (info.status === "loading") {
		return (
			<Shimmer $height="44px" $radius="8px">
				{day}
			</Shimmer>
		);
	}

	const interactive = info.status !== "future";
	const hasChargebacks = info.status === "chargebacks" && !!info.entry;
	const cell = (
		<Box
			onClick={interactive ? onSelect : undefined}
			sx={{
				position: "relative",
				height: "44px",
				borderRadius: "8px",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				boxSizing: "border-box",
				backgroundColor: hasChargebacks ? getChargebackTint(info.entry!.count) : "transparent",
				border: info.status === "open" ? `1px dashed #d0d5dd` : "1px solid transparent",
				boxShadow: isSelected ? `0 0 0 2px ${COLORS.selected}` : "none",
				color: info.status === "future" ? "#d0d5dd" : isToday ? COLORS.today : hasChargebacks ? "#b42318" : COLORS.text,
				cursor: interactive ? "pointer" : "default",
				userSelect: "none",
				transition: "background-color .12s ease",
				"&:hover": interactive && !hasChargebacks ? { backgroundColor: "#f2f4f7" } : {},
			}}>
			<Box sx={{ fontSize: "13px", lineHeight: "16px", fontWeight: isToday || hasChargebacks ? 600 : 400 }}>{day}</Box>
			{hasChargebacks && <Box sx={{ fontSize: "10px", lineHeight: "12px", fontWeight: 600, opacity: 0.75 }}>{info.entry!.count}</Box>}
			{info.status === "missing" && (
				<Box sx={{ position: "absolute", bottom: "6px", width: "4px", height: "4px", borderRadius: "50%", backgroundColor: COLORS.amber }} />
			)}
		</Box>
	);

	const tooltip = hasChargebacks
		? `${msg("chargebacks_4b3fbb67")}${info.entry!.count} · ${cents(info.amount)} EUR`
		: info.status === "missing"
			? msg("cbNoFileDay")
			: info.status === "open"
				? msg("cbDayOpen")
				: undefined;

	return tooltip ? (
		<HoverTooltip content={<Box sx={{ color: "#fff", fontSize: "12px", lineHeight: "18px", whiteSpace: "nowrap" }}>{tooltip}</Box>} style={{ display: "block" }}>
			{cell}
		</HoverTooltip>
	) : (
		cell
	);
};

const MONTH_WIDTH = 7 * 44 + 6 * 4;
const MONTH_GAP = 40;
const MONTH_STEP = MONTH_WIDTH + MONTH_GAP;
// Room around the track for the selected day's outline, which the clipping viewport would otherwise cut.
const TRACK_PADDING = 4;

type TMonthSlide = { direction: "forward" | "back"; outgoing: TMonthKey & { data: TMonthData } };

const MonthCalendar = ({
	year,
	month,
	data,
	today,
	selection,
	onSelect,
}: {
	year: number;
	month: number;
	data: TMonthData;
	today: Date;
	selection?: TDaySelection;
	onSelect: (selection: TDaySelection) => void;
}) => {
	useLocale();
	const offset = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
	const todayStamp = utcDayStamp(today);

	return (
		<VBox gap="12px" sx={{ width: `${MONTH_WIDTH}px`, flexShrink: 0 }}>
			<HBox sx={{ alignItems: "baseline", justifyContent: "center", gap: "6px" }}>
				<Typo sx={{ fontSize: "14px", lineHeight: "20px", fontWeight: 600, color: COLORS.title }}>{monthLabel(year, month)}</Typo>
				<Typo sx={{ fontSize: "13px", lineHeight: "20px", color: COLORS.muted }}>{year}</Typo>
			</HBox>

			<Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 44px)", gap: "4px" }}>
				{getWeekdays().map(weekday => (
					<Box key={weekday} sx={{ textAlign: "center", fontSize: "11px", lineHeight: "20px", color: COLORS.muted }}>
						{weekday}
					</Box>
				))}
				{Array.from({ length: offset }, (_, i) => (
					<Box key={`pad-${i}`} />
				))}
				{Array.from({ length: daysInMonth(year, month) }, (_, i) => {
					const day = i + 1;
					return (
						<DayCell
							key={day}
							day={day}
							info={getDayInfo(year, month, day, data, today)}
							isToday={Date.UTC(year, month, day) === todayStamp}
							isSelected={!!selection && isSameMonth(selection, { year, month }) && selection.day === day}
							onSelect={() => onSelect({ year, month, day })}
						/>
					);
				})}
			</Box>

			{data.failed && <Typo sx={{ textAlign: "center", fontSize: "12px", color: COLORS.red }}>{msg("cbLoadFailed")}</Typo>}
		</VBox>
	);
};

const CalendarLegend = () => {
	useLocale();
	const item = (marker: ReactNode, label: string) => (
		<HBox sx={{ alignItems: "center", gap: "6px" }}>
			{marker}
			{label}
		</HBox>
	);

	return (
		<HBox sx={{ gap: "18px", flexWrap: "wrap", justifyContent: "center", fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>
			{item(<Box sx={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#fee4e2" }} />, msg("cbLegendHasChargebacks"))}
			{item(<Box sx={{ width: "5px", height: "5px", margin: "0 2px", borderRadius: "50%", backgroundColor: COLORS.amber }} />, msg("cbLegendNoFile"))}
		</HBox>
	);
};

// Full-height edge strip of the calendar card, separated from the months by a hairline; the whole strip is clickable.
const SideArrow = ({ direction, disabled, onClick }: { direction: "left" | "right"; disabled?: boolean; onClick: () => void }) => (
	<Box
		onClick={disabled ? undefined : onClick}
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			alignSelf: "stretch",
			flexShrink: 0,
			width: "52px",
			backgroundColor: "#fcfcfd",
			[direction === "left" ? "borderRight" : "borderLeft"]: `1px solid ${COLORS.border}`,
			color: disabled ? "#d0d5dd" : COLORS.secondary,
			cursor: disabled ? "default" : "pointer",
			userSelect: "none",
			transition: "background-color .15s, color .15s",
			"&:hover": disabled ? {} : { backgroundColor: "#f2f4f7", color: COLORS.title },
			"&:active": disabled ? {} : { backgroundColor: COLORS.border },
		}}>
		{direction === "left" ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
	</Box>
);

const formatDelta = (current: number, previous: number) => {
	if (previous === 0) return undefined;
	const pct = Math.round(((current - previous) / previous) * 100);
	return { text: `${pct > 0 ? "+" : ""}${pct}%`, color: pct > 0 ? COLORS.red : pct < 0 ? COLORS.green : COLORS.muted };
};

const SummaryItem = ({ label, value, valueColor, delta, deltaTitle }: { label: string; value: ReactNode; valueColor?: string; delta?: { text: string; color: string }; deltaTitle?: string }) => (
	<VBox gap="2px" sx={{ minWidth: 0 }}>
		<Typo sx={{ fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>{label}</Typo>
		<HBox sx={{ alignItems: "baseline", gap: "8px" }}>
			<Box sx={{ fontSize: "18px", lineHeight: "26px", fontWeight: 600, color: valueColor ?? COLORS.title, whiteSpace: "nowrap" }}>{value}</Box>
			{delta && (
				<Box title={deltaTitle} sx={{ fontSize: "12px", lineHeight: "18px", fontWeight: 500, color: delta.color, cursor: "help" }}>
					{delta.text}
				</Box>
			)}
		</HBox>
	</VBox>
);

const MonthSummary = ({
	view,
	summary,
	previous,
	isCurrentMonth,
	failed,
}: {
	view: TMonthKey;
	summary?: TMonthSummary;
	previous?: TMonthSummary;
	isCurrentMonth: boolean;
	failed: boolean;
}) => {
	useLocale();
	const deltaTitle = isCurrentMonth ? msg("cbVsSamePeriod") : msg("cbVsPrevMonth");
	const placeholder = (width: string) => (failed ? "—" : <Shimmer $width={width} $height="20px" style={{ margin: "3px 0" }} />);

	return (
		<HBox
			sx={{
				...CARD_SX,
				padding: "0",
				alignItems: "stretch",
				flexWrap: "wrap",
				overflow: "hidden",
				"& > div": { padding: "14px 28px" },
				"& > div + div": { borderLeft: "1px solid #d0d5dd" },
			}}>
			<SummaryItem label={msg("cbKpiPeriod")} value={`${monthLabel(view.year, view.month)} ${view.year}`} />
			<SummaryItem
				label={msg("cbKpiChargebacks")}
				value={summary ? summary.count : placeholder("40px")}
				delta={summary && previous ? formatDelta(summary.count, previous.count) : undefined}
				deltaTitle={deltaTitle}
			/>
			<SummaryItem
				label={msg("cbKpiAmount")}
				value={summary ? `${cents(summary.amount)} EUR` : placeholder("110px")}
				delta={summary && previous ? formatDelta(summary.amount, previous.amount) : undefined}
				deltaTitle={deltaTitle}
			/>
			<SummaryItem
				label={msg("cbKpiMissingFiles")}
				value={summary ? summary.missingDays : placeholder("32px")}
				valueColor={summary && summary.missingDays > 0 ? "#b54708" : undefined}
			/>
		</HBox>
	);
};

const DayMessage = ({ icon, title, hint }: { icon: ReactNode; title: string; hint?: string }) => (
	<VBox gap="6px" sx={{ alignItems: "center", textAlign: "center", padding: "32px 24px", color: COLORS.muted }}>
		{icon}
		<Typo sx={{ fontSize: "13px", lineHeight: "20px", fontWeight: 600, color: COLORS.text, marginTop: "4px" }}>{title}</Typo>
		{hint && <Typo sx={{ fontSize: "12px", lineHeight: "18px", color: COLORS.secondary, maxWidth: "240px" }}>{hint}</Typo>}
	</VBox>
);

const DayChargebackRow = ({ chargeback, onClick }: { chargeback: IChargebackEntry; onClick: () => void }) => {
	useLocale();
	return (
		<HBox
			onClick={onClick}
			sx={{
				alignItems: "center",
				gap: "12px",
				padding: "10px 20px",
				cursor: "pointer",
				transition: "background-color .15s",
				"&:hover": { backgroundColor: "#f9fafb" },
			}}>
			<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
				<Typo sx={{ fontSize: "13px", lineHeight: "20px", fontWeight: 600, color: COLORS.title }}>{cents(chargeback.amount)} EUR</Typo>
				<HBox sx={{ gap: "4px", alignItems: "center", fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>
					{msg("paymentId_96c93539")}
					<CopyableValue value={chargeback.payment_id} />
				</HBox>
			</VBox>
			{chargeback.status && <Typo sx={{ fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>{capitalize(chargeback.status)}</Typo>}
			<ChevronRight size={16} color="#d0d5dd" />
		</HBox>
	);
};

const DayPanelSkeleton = () => (
	<VBox sx={{ ...CARD_SX, minWidth: 0, overflow: "hidden" }}>
		<VBox gap="8px" sx={{ padding: "18px 20px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
			<Shimmer $width="58%" $height="16px" />
			<Shimmer $width="38%" $height="10px" />
		</VBox>
		<VBox gap="18px" sx={{ padding: "16px 20px 20px" }}>
			{[72, 64, 80].map(width => (
				<HBox key={width} sx={{ alignItems: "center", gap: "12px" }}>
					<VBox gap="7px" sx={{ flex: 1, minWidth: 0 }}>
						<Shimmer $width="34%" $height="13px" />
						<Shimmer $width={`${width}%`} $height="10px" />
					</VBox>
					<Shimmer $width="48px" $height="10px" />
				</HBox>
			))}
		</VBox>
	</VBox>
);

const DayPanel = ({
	selection,
	info,
	loading,
	onOpenChargeback,
	onOpenFile,
}: {
	selection?: TDaySelection;
	info?: TDayInfo;
	loading: boolean;
	onOpenChargeback: (chargeback: IChargebackEntry) => void;
	onOpenFile: () => void;
}) => {
	useLocale();

	if (loading || info?.status === "loading") return <DayPanelSkeleton />;

	if (!selection || !info) {
		return (
			<Box sx={CARD_SX}>
				<DayMessage icon={<CalendarDays size={20} />} title={msg("cbSelectDayHint")} />
			</Box>
		);
	}

	const title = capitalize(
		new Date(Date.UTC(selection.year, selection.month, selection.day)).toLocaleDateString(localeTag(), {
			weekday: "long",
			day: "numeric",
			month: "long",
			timeZone: "UTC",
		}),
	);
	const entry = info.entry;
	const hasChargebacks = info.status === "chargebacks" && !!entry;

	return (
		<VBox sx={{ ...CARD_SX, minWidth: 0, overflow: "hidden" }}>
			<HBox sx={{ alignItems: "flex-start", gap: "12px", padding: "16px 20px", borderBottom: hasChargebacks ? `1px solid ${COLORS.border}` : "none" }}>
				<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
					<Typo sx={{ fontSize: "15px", lineHeight: "22px", fontWeight: 600, color: COLORS.title }}>{title}</Typo>
					{hasChargebacks && (
						<Typo sx={{ fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>
							{msg("chargebacks_4b3fbb67")}
							{entry.count} · {cents(info.amount)} EUR
						</Typo>
					)}
				</VBox>
				{entry && (
					<Box
						onClick={onOpenFile}
						title={msg("cbOpenFile")}
						sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", lineHeight: "22px", color: COLORS.secondary, cursor: "pointer", whiteSpace: "nowrap", "&:hover": { color: COLORS.title } }}>
						<FileJson size={14} />
						{msg("cbOpenFile")}
					</Box>
				)}
			</HBox>

			{hasChargebacks ? (
				<Box sx={{ maxHeight: "460px", overflowY: "auto", padding: "4px 0" }}>
					{entry.chargebacks.map(chargeback => (
						<DayChargebackRow key={chargeback.id} chargeback={chargeback} onClick={() => onOpenChargeback(chargeback)} />
					))}
				</Box>
			) : info.status === "clean" ? (
				<DayMessage icon={<CircleCheck size={20} color={COLORS.green} />} title={msg("cbNoChargebacksDay")} />
			) : info.status === "missing" ? (
				<DayMessage icon={<FileWarning size={20} color={COLORS.amber} />} title={msg("cbNoFileDay")} hint={msg("cbNoFileDayHint")} />
			) : info.status === "open" ? (
				<DayMessage icon={<CalendarDays size={20} />} title={msg("cbDayOpen")} hint={msg("cbDayOpenHint")} />
			) : info.status === "failed" ? (
				<DayMessage icon={<AlertCircle size={20} color={COLORS.red} />} title={msg("cbLoadFailed")} hint={msg("cbLoadFailedHint")} />
			) : null}
		</VBox>
	);
};

const findLatestChargebackDay = (months: (TMonthKey & { data: TMonthData })[], today: Date): TDaySelection | undefined => {
	for (const { year, month, data } of [...months].reverse()) {
		for (let day = daysInMonth(year, month); day >= 1; day--) {
			if (getDayInfo(year, month, day, data, today).status === "chargebacks") return { year, month, day };
		}
	}
	return undefined;
};

export const Chargebacks = () => {
	useLocale();
	const today = new Date();
	const currentMonth: TMonthKey = { year: today.getUTCFullYear(), month: today.getUTCMonth() };

	const [view, setView] = useState<TMonthKey>(currentMonth);
	const [selection, setSelection] = useState<TDaySelection | undefined>(undefined);
	const [openedChargeback, setOpenedChargeback] = useState<{ entry: IChargebackDayEntry; chargeback: IChargebackEntry } | undefined>(undefined);
	const [fileEntry, setFileEntry] = useState<IChargebackDayEntry | undefined>(undefined);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);
	const [slide, setSlide] = useState<TMonthSlide | undefined>(undefined);
	const trackRef = useRef<HTMLDivElement>(null);

	const previousMonth = shiftMonth(view, -1);
	const previousData = useChargebackMonth(previousMonth.year, previousMonth.month, reloadKey);
	const viewData = useChargebackMonth(view.year, view.month, reloadKey);
	const months = [
		{ ...previousMonth, data: previousData },
		{ ...view, data: viewData },
	];
	const isAtCurrent = isSameMonth(view, currentMonth);

	// While sliding, the track holds three months: the visible pair plus the one leaving. The leaving month keeps the data
	// it was showing at the click, so nothing is refetched mid-animation; the pair itself always comes from the live hooks.
	const trackMonths = !slide
		? months
		: slide.direction === "forward"
			? [slide.outgoing, ...months]
			: [...months, slide.outgoing];

	useLayoutEffect(() => {
		const track = trackRef.current;
		if (!slide || !track) return;
		const [from, to] = slide.direction === "forward" ? [0, -MONTH_STEP] : [-MONTH_STEP, 0];
		const animation = track.animate([{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }], {
			duration: 420,
			easing: "cubic-bezier(.22,.8,.3,1)",
			fill: "forwards",
		});
		animation.onfinish = () => setSlide(current => (current === slide ? undefined : current));
		// Cancelled in the same commit that drops the leaving month, so the final frame never jumps.
		return () => animation.cancel();
	}, [slide]);

	const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

	const goBack = () => {
		setSlide(reducedMotion() ? undefined : { direction: "back", outgoing: { ...view, data: viewData } });
		setView(shiftMonth(view, -1));
	};

	const goForward = () => {
		if (isAtCurrent) return;
		setSlide(reducedMotion() ? undefined : { direction: "forward", outgoing: { ...previousMonth, data: previousData } });
		setView(shiftMonth(view, 1));
	};

	const visibleMonth = (target: TMonthKey) => months.find(item => isSameMonth(item, target));
	const fallbackSelection = findLatestChargebackDay(months, today) ?? (isAtCurrent ? { ...currentMonth, day: today.getUTCDate() } : undefined);
	const activeSelection = selection && visibleMonth(selection) ? selection : fallbackSelection;
	const activeMonth = activeSelection && visibleMonth(activeSelection);
	const activeInfo = activeSelection && activeMonth ? getDayInfo(activeSelection.year, activeSelection.month, activeSelection.day, activeMonth.data, today) : undefined;
	// Until both months load, the automatic pick may still move, so the panel waits instead of flashing a placeholder day.
	const panelLoading = !(selection && visibleMonth(selection)) && months.some(item => !item.data.entries && !item.data.failed);

	const viewSummary = summarizeMonth(view.year, view.month, viewData, today);
	const previousSummary = viewSummary
		? summarizeMonth(previousMonth.year, previousMonth.month, previousData, today, isAtCurrent ? viewSummary.elapsedDays : undefined)
		: undefined;

	const handleCreated = (chargebackDate: string) => {
		const [year, month, day] = chargebackDate.split("-").map(Number);
		if (year && month) {
			setSlide(undefined);
			setView({ year, month: month - 1 });
			if (day) setSelection({ year, month: month - 1, day });
		}
		monthCache.clear();
		setReloadKey(value => value + 1);
		setShowCreateForm(false);
	};

	return (
		<VBox gap="16px" sx={{ padding: "20px", minWidth: 0, flex: 1, backgroundColor: "#f5f7fa" }}>
			<HBox sx={{ alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
				<VBox gap="2px">
					<Typo sx={{ fontSize: "15px", lineHeight: "20px", fontWeight: 600, color: COLORS.title }}>{msg("chargebackCalendar")}</Typo>
					<Typo sx={{ color: COLORS.secondary, fontSize: "12px", lineHeight: "17px" }}>{msg("trackDisputesReceivedFromCardSchemesAndProcessors")}</Typo>
				</VBox>
				<Spacer grow />
				<Button title={msg("addChargeback")} variant="primarySmall" icon={<Plus size={15} />} onClick={() => setShowCreateForm(true)} />
			</HBox>

			<MonthSummary view={view} summary={viewSummary} previous={previousSummary} isCurrentMonth={isAtCurrent} failed={viewData.failed} />

			<HBox sx={{ gap: "16px", alignItems: "flex-start", flexWrap: "wrap", minWidth: 0 }}>
				<HBox sx={{ ...CARD_SX, flex: "0 1 816px", minWidth: 0, alignItems: "stretch", overflow: "hidden" }}>
					<SideArrow direction="left" onClick={goBack} />
					<VBox gap="16px" sx={{ flex: 1, minWidth: 0, padding: "16px 0 20px", overflowX: "auto" }}>
						{/* Two months wide; the track inside slides by one month and anything outside is clipped. */}
						<Box sx={{ margin: "0 auto", flexShrink: 0, width: `${2 * MONTH_WIDTH + MONTH_GAP + 2 * TRACK_PADDING}px`, overflow: "hidden" }}>
							<Box ref={trackRef} sx={{ display: "flex", gap: `${MONTH_GAP}px`, width: "max-content", padding: `${TRACK_PADDING}px`, willChange: slide ? "transform" : undefined }}>
								{trackMonths.map(item => (
									<MonthCalendar
										key={`${item.year}-${item.month}`}
										year={item.year}
										month={item.month}
										data={item.data}
										today={today}
										selection={activeSelection}
										onSelect={setSelection}
									/>
								))}
							</Box>
						</Box>
						<CalendarLegend />
					</VBox>
					<SideArrow direction="right" disabled={isAtCurrent} onClick={goForward} />
				</HBox>

				<Box sx={{ flex: "1 1 340px", minWidth: "280px" }}>
					<DayPanel
						selection={activeSelection}
						info={activeInfo}
						loading={panelLoading}
						onOpenChargeback={chargeback => activeInfo?.entry && setOpenedChargeback({ entry: activeInfo.entry, chargeback })}
						onOpenFile={() => activeInfo?.entry && setFileEntry(activeInfo.entry)}
					/>
				</Box>
			</HBox>

			{openedChargeback && (
				<ChargebackDetailsLoader entry={openedChargeback.entry} chargeback={openedChargeback.chargeback} onClose={() => setOpenedChargeback(undefined)} />
			)}
			{fileEntry && <ChargebackFilePopup entry={fileEntry} onClose={() => setFileEntry(undefined)} />}
			{showCreateForm && <CreateChargebackPopup onClose={() => setShowCreateForm(false)} onCreated={handleCreated} />}
		</VBox>
	);
};

type TChargebackForm = {
	paymentId: string;
	dectaPaymentId: string;
	merchantOrderId: string;
	arn: string;
	network: string;
	category: string;
	reasonCode: string;
	referenceNumber: string;
	amount: string;
	currency: string;
	originalAmount: string;
	originalCurrency: string;
	feeAmount: string;
	feeCurrency: string;
	transactionDate: string;
	chargebackDate: string;
	responseDueDate: string;
	disputeStatus: string;
	processingStatus: string;
	issuerCountry: string;
	descriptor: string;
	cardLast4: string;
	rdr: string;
	comments: string;
};

type TSelectOption = { label: string; value: string };

const NETWORK_OPTIONS: TSelectOption[] = [
	{ label: "Visa", value: "visa" },
	{ label: "Mastercard", value: "mastercard" },
	{ label: "American Express", value: "amex" },
	{ label: "Discover / Diners Club", value: "discover" },
	{ label: "UnionPay", value: "unionpay" },
	{ label: "JCB", value: "jcb" },
	{ get label() { return msg("other"); }, value: "other" },
];

const CATEGORY_OPTIONS: TSelectOption[] = [
	{ get label() { return msg("fraud"); }, value: "fraud" },
	{ get label() { return msg("authorization"); }, value: "authorization" },
	{ get label() { return msg("processingError"); }, value: "processing_error" },
	{ get label() { return msg("consumerDispute"); }, value: "consumer_dispute" },
];

const CURRENCY_OPTIONS: TSelectOption[] = ["EUR", "USD", "GBP", "BGN", "CHF", "PLN", "RON"].map(value => ({ label: value, value }));
const DISPUTE_STATUS_OPTIONS: TSelectOption[] = [
	{ get label() { return msg("received"); }, value: "received" },
	{ get label() { return msg("underReview"); }, value: "under_review" },
	{ get label() { return msg("accepted"); }, value: "accepted" },
	{ get label() { return msg("representment"); }, value: "representment" },
];
const PROCESSING_STATUS_OPTIONS: TSelectOption[] = [
	{ get label() { return msg("success"); }, value: "success" },
	{ get label() { return msg("pending"); }, value: "pending" },
	{ get label() { return msg("failed_031a8f0f"); }, value: "failed" },
];
const YES_NO_OPTIONS: TSelectOption[] = [
	{ get label() { return msg("no"); }, value: "false" },
	{ get label() { return msg("yes"); }, value: "true" },
];

const toDateInputValue = (date: Date) =>
	`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;

const createInitialChargebackForm = (): TChargebackForm => ({
	paymentId: "",
	dectaPaymentId: "",
	merchantOrderId: "",
	arn: "",
	network: "visa",
	category: "fraud",
	reasonCode: "",
	referenceNumber: "",
	amount: "",
	currency: "EUR",
	originalAmount: "",
	originalCurrency: "EUR",
	feeAmount: "",
	feeCurrency: "EUR",
	transactionDate: "",
	chargebackDate: toDateInputValue(new Date()),
	responseDueDate: "",
	disputeStatus: "received",
	processingStatus: "success",
	issuerCountry: "",
	descriptor: "",
	cardLast4: "",
	rdr: "false",
	comments: "",
});

const parseFormAmount = (value: string) => Number(value.replaceAll(" ", "").replace(",", "."));
const looksLikeScientificNotation = (value: string) => /^[+-]?(?:\d+\.?\d*|\.\d+)[eE][+-]?\d+$/.test(value.trim());
const CHARGEBACK_PARSER_URL = "https://valera.impaya.com/api/v1/documents/parse";
const CHARGEBACK_PARSER_AUTH = VALERA_AUTH;

const normalizeParserKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
const parserValue = (record: Record<string, unknown>, ...aliases: string[]) => {
	const normalized = new Map(Object.entries(record).map(([key, value]) => [normalizeParserKey(key), value]));
	for (const alias of aliases) {
		const value = normalized.get(normalizeParserKey(alias));
		if (value !== undefined && value !== null && value !== "") return value;
	}
	return undefined;
};

const collectParserRecords = (value: unknown, records: Record<string, unknown>[] = [], depth = 0): Record<string, unknown>[] => {
	if (depth > 8 || value === null || value === undefined) return records;
	if (typeof value === "string" && /^[\s]*[[{]/.test(value)) {
		try {
			collectParserRecords(JSON.parse(value), records, depth + 1);
		} catch {
			// Plain strings are valid leaf values in parser responses.
		}
		return records;
	}
	if (Array.isArray(value)) {
		value.forEach(item => collectParserRecords(item, records, depth + 1));
		return records;
	}
	if (typeof value !== "object") return records;

	const record = value as Record<string, unknown>;
	if (Object.keys(record).length) records.push(record);
	Object.values(record).forEach(item => {
		if (item && (typeof item === "object" || (typeof item === "string" && /^[\s]*[[{]/.test(item)))) {
			collectParserRecords(item, records, depth + 1);
		}
	});
	return records;
};

const asParserRecords = (value: unknown): Record<string, unknown>[] =>
	Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item)) : [];

const getParserTransactions = (payload: unknown): Record<string, unknown>[] => {
	if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
	const envelope = payload as Record<string, unknown>;
	const data = envelope.data && typeof envelope.data === "object" && !Array.isArray(envelope.data) ? (envelope.data as Record<string, unknown>) : envelope;

	const directTransactions = asParserRecords(data.transactions);
	if (directTransactions.length) return directTransactions;

	const documentTransactions = asParserRecords(data.documents).flatMap(document => asParserRecords(document.transactions));
	if (documentTransactions.length) return documentTransactions;

	// Fallback for older parser response formats. Never select a nested `raw`
	// source row when a normalized record that owns it is present.
	const records = collectParserRecords(payload);
	const rawRecords = new Set(
		records
			.map(record => record.raw)
			.filter((record): record is Record<string, unknown> => Boolean(record) && typeof record === "object" && !Array.isArray(record)),
	);
	return records.filter(record => !rawRecords.has(record));
};

const getParserWarnings = (payload: unknown): string[] => {
	const warnings = collectParserRecords(payload).flatMap(record =>
		Array.isArray(record.warnings) ? record.warnings.filter((warning): warning is string => typeof warning === "string" && Boolean(warning.trim())) : [],
	);
	return [...new Set(warnings)];
};

const parserRecordScore = (record: Record<string, unknown>) => {
	const operationType = String(parserValue(record, "type", "operation_type", "operationType") ?? "").toLowerCase();
	let score = operationType.includes("chargeback") ? 12 : /refund|transfer/.test(operationType) ? -12 : 0;
	if (parserValue(record, "reason_code", "reasonCode")) score += 4;
	if (parserValue(record, "arn")) score += 3;
	if (parserValue(record, "pan", "card", "card_mask", "maskedPan")) score += 2;
	if (parserValue(record, "card_type", "card_network", "network", "scheme", "cardBrand")) score += 2;
	if (parserValue(record, "payment_id", "decta_payment_id", "order_id", "merchant_order_id")) score += 2;
	if (parserValue(record, "amount", "chb_amount", "chargeback_amount")) score += 1;
	return score;
};

const parserText = (value: unknown) => (typeof value === "string" || typeof value === "number" ? String(value).trim() : "");
const normalizeParserDate = (value: unknown) => {
	const text = parserText(value);
	const yearFirst = text.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
	if (yearFirst) return `${yearFirst[1]}-${yearFirst[2].padStart(2, "0")}-${yearFirst[3].padStart(2, "0")}`;
	const dayFirst = text.match(/^(\d{1,2})[-./](\d{1,2})[-./](\d{4})/);
	if (dayFirst) return `${dayFirst[3]}-${dayFirst[2].padStart(2, "0")}-${dayFirst[1].padStart(2, "0")}`;
	return "";
};

const normalizeNetwork = (value: unknown) => {
	const text = parserText(value).toLowerCase();
	if (text.includes("master")) return "mastercard";
	if (text.includes("visa")) return "visa";
	if (text.includes("american") || text.includes("amex")) return "amex";
	if (text.includes("discover") || text.includes("diners")) return "discover";
	if (text.includes("union")) return "unionpay";
	if (text.includes("jcb")) return "jcb";
	return text ? "other" : "";
};

const normalizeCategory = (value: unknown, network: string, reasonCode: string) => {
	const text = parserText(value).toLowerCase().replaceAll(" ", "_");
	if (["fraud", "authorization", "processing_error", "consumer_dispute"].includes(text)) return text;
	if (network === "visa") {
		if (reasonCode.startsWith("10.")) return "fraud";
		if (reasonCode.startsWith("11.")) return "authorization";
		if (reasonCode.startsWith("12.")) return "processing_error";
		if (reasonCode.startsWith("13.")) return "consumer_dispute";
	}
	return "";
};

const normalizeProcessingStatus = (value: unknown) => {
	const text = parserText(value).toLowerCase();
	if (["success", "completed", "complete", "processed"].includes(text)) return "success";
	if (["failed", "error", "rejected"].includes(text)) return "failed";
	if (["pending", "processing"].includes(text)) return "pending";
	return "";
};

const mapParserRecordToForm = (record: Record<string, unknown>) => {
	const warnings: string[] = [];
	const precisionLoss = new Set(
		(Array.isArray(record.precisionLoss) ? record.precisionLoss : []).map(field => normalizeParserKey(String(field))),
	);
	const exactIdentifier = (label: string, value: unknown, precisionAliases: string[] = []) => {
		const text = parserText(value);
		if (text && precisionAliases.some(alias => precisionLoss.has(normalizeParserKey(alias)))) {
			warnings.push(msg("templateWasSkippedBecauseTheParserReportedPrecisionLossInTheSour_a93beaad", { v0: label }));
			return "";
		}
		if (text && looksLikeScientificNotation(text)) {
			warnings.push(msg("templateWasSkippedBecauseTheSourceContainsScientificNotation_18128990", { v0: label }));
			return "";
		}
		return text;
	};
	const reasonCode = parserText(parserValue(record, "reason_code", "reasonCode"));
	const network = normalizeNetwork(parserValue(record, "card_network", "card_type", "network", "scheme", "cardBrand"));
	const pan = parserText(parserValue(record, "pan", "card", "card_mask", "maskedPan"));
	const panDigits = pan.replace(/\D/g, "");
	const explicitPaymentId = parserValue(record, "payment_id", "paymentId");
	const merchantOrderId = parserValue(record, "merchant_order_id", "merchantOrderId");
	const raw = record.raw && typeof record.raw === "object" && !Array.isArray(record.raw) ? (record.raw as Record<string, unknown>) : undefined;
	const mapped: Partial<TChargebackForm> = {
		// In Decta exports our payment ID is carried in merchant_order_id, while
		// order_id is Decta's processor-side identifier.
		paymentId: exactIdentifier(msg("paymentId_d9c9afd8"), explicitPaymentId ?? merchantOrderId, explicitPaymentId === undefined ? ["merchantOrderId"] : ["paymentId"]),
		dectaPaymentId: exactIdentifier(msg("dectaPaymentId"), parserValue(record, "decta_payment_id", "dectaPaymentId", "order_id", "orderId")).replace(/^\.\/(?=\d)/, ""),
		merchantOrderId: explicitPaymentId === undefined ? "" : exactIdentifier(msg("merchantOrderId"), merchantOrderId, ["merchantOrderId"]),
		arn: exactIdentifier("ARN", parserValue(record, "arn"), ["arn"]),
		network,
		category: normalizeCategory(parserValue(record, "dispute_category", "category"), network, reasonCode),
		reasonCode,
		referenceNumber: exactIdentifier(msg("chargebackReference"), parserValue(record, "chargeback_reference_number", "chb_refer_num", "reference_number", "reference_id")),
		amount: parserText(parserValue(record, "chargeback_amount", "chb_amount", "amount")),
		currency: parserText(parserValue(record, "chargeback_currency", "chb_ccy", "currency")).toUpperCase(),
		originalAmount: parserText(parserValue(record, "transaction_amount", "tr_amount", "base_amount", "accounting_amount")),
		originalCurrency: parserText(parserValue(record, "transaction_currency", "tr_ccy", "base_currency", "accounting_currency")).toUpperCase(),
		feeAmount: parserText(parserValue(record, "chargeback_fee_amount", "merchant_fee_amount", "fee_amount")),
		feeCurrency: parserText(parserValue(record, "chargeback_fee_currency", "merchant_fee_currency", "fee_currency")).toUpperCase(),
		transactionDate: normalizeParserDate(parserValue(record, "transaction_date", "tr_date_time", "actual_date", "valueDate")),
		chargebackDate: normalizeParserDate(parserValue(record, "chargeback_date", "chb_tr_date", "created", "createdAt")),
		responseDueDate: normalizeParserDate(parserValue(record, "response_due_date", "due_date")),
		processingStatus: normalizeProcessingStatus(parserValue(record, "status", "processing_status")),
		issuerCountry: parserText(parserValue(record, "issuer_country", "issuerCountry")).toUpperCase(),
		descriptor: parserText(parserValue(record, "merchant_descriptor", "descriptor")),
		cardLast4: panDigits.length >= 4 ? panDigits.slice(-4) : "",
		rdr: ["true", "1", "yes"].includes(parserText(parserValue(record, "rdr") ?? (raw ? parserValue(raw, "rdr") : undefined)).toLowerCase()) ? "true" : "false",
		comments: parserText(parserValue(record, "comments", "comment", "notes")),
	};

	const values = Object.fromEntries(Object.entries(mapped).filter(([, value]) => value !== "" && value !== undefined)) as Partial<TChargebackForm>;
	return { values, warnings };
};

const fileToDataUri = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error(msg("theSelectedFileCouldNotBeRead")));
		reader.onload = () => (typeof reader.result === "string" ? resolve(reader.result) : reject(new Error(msg("theSelectedFileCouldNotBeEncoded"))));
		reader.readAsDataURL(file);
	});

const CreateChargebackPopup = ({ onClose, onCreated }: { onClose: () => void; onCreated: (chargebackDate: string) => void }) => {
	useLocale();
	const [form, setForm] = useState<TChargebackForm>(createInitialChargebackForm);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError | undefined>(undefined);
	const [parserFile, setParserFile] = useState<File | null>(null);
	const [parserBusy, setParserBusy] = useState(false);
	const [parserError, setParserError] = useState<IError | undefined>(undefined);
	const [parserMessage, setParserMessage] = useState<string | undefined>(undefined);

	const todayISO = toUTCISODate(new Date());
	const updateField = (name: keyof TChargebackForm, value: string) => {
		setForm(current => ({ ...current, [name]: value }));
		setError(undefined);
	};
	const updateNetwork = (network: string) => {
		setForm(current => ({ ...current, network, rdr: network === "visa" ? current.rdr : "false" }));
		setError(undefined);
	};

	const close = () => {
		if (busy || parserBusy) return;
		onClose();
	};

	const parseDocument = async () => {
		if (!parserFile) {
			setParserError({ code: 0, message: msg("selectACsvOrExcelFileFirst") });
			return;
		}
		if (parserFile.size > 10 * 1024 * 1024) {
			setParserError({ code: 0, message: msg("theParserAcceptsFilesUpTo10Mb") });
			return;
		}

		setParserBusy(true);
		setParserError(undefined);
		setParserMessage(undefined);
		try {
			const dataUri = await fileToDataUri(parserFile);
			const response = await fetch(CHARGEBACK_PARSER_URL, {
				method: "POST",
				headers: {
					Authorization: CHARGEBACK_PARSER_AUTH,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ documents: [{ name: parserFile.name, data: dataUri }] }),
			});
			const responseText = await response.text();
			let payload: unknown = responseText;
			try {
				payload = responseText ? JSON.parse(responseText) : null;
			} catch {
				// Some parser implementations may return plain text.
			}

			if (!response.ok) {
				const apiMessage =
					payload && typeof payload === "object" && "error" in payload && payload.error && typeof payload.error === "object" && "message" in payload.error
						? String(payload.error.message)
						: msg("templateParserRequestFailedWithStatus_d4331fa4", { v0: response.status });
				throw new Error(apiMessage);
			}

			const candidates = getParserTransactions(payload)
				.map(record => ({ record, score: parserRecordScore(record) }))
				.filter(candidate => candidate.score >= 4)
				.sort((left, right) => right.score - left.score);
			if (!candidates.length) {
				throw new Error(msg("noCardChargebackRecordWasFoundRefundAndBankTransferFilesAreNotSup"));
			}

			const mapped = mapParserRecordToForm(candidates[0].record);
			const fieldCount = Object.keys(mapped.values).length;
			if (!fieldCount) throw new Error(msg("theParserReturnedAChargebackButNoSupportedFormFieldsWereFound"));

			setForm(current => ({ ...current, ...mapped.values }));
			const multipleRecordsNote = candidates.length > 1 ? msg("templatePossibleChargebackRecordsWereFoundTheBestMatchWasLoaded_3fce2d33", { v0: candidates.length }) : "";
			const parserWarnings = getParserWarnings(payload);
			const warningsNote = [...new Set([...mapped.warnings, ...parserWarnings])].length
				? ` ${[...new Set([...mapped.warnings, ...parserWarnings])].join(" ")}`
				: "";
			setParserMessage(msg("templateFieldsFilledFromReviewTheValuesBeforeCreatingTheChargeba_a0392097", { v0: fieldCount, v1: parserFile.name, v2: multipleRecordsNote, v3: warningsNote }));
		} catch (parseError) {
			const message = parseError instanceof Error ? parseError.message : msg("theDocumentCouldNotBeParsed");
			setParserError({ code: 0, message });
		} finally {
			setParserBusy(false);
		}
	};

	const createChargeback = async () => {
		const amount = parseFormAmount(form.amount);
		const originalAmount = form.originalAmount ? parseFormAmount(form.originalAmount) : undefined;
		const feeAmount = form.feeAmount ? parseFormAmount(form.feeAmount) : undefined;
		const requiredFields = [form.paymentId, form.dectaPaymentId, form.network, form.category, form.reasonCode, form.amount, form.transactionDate, form.chargebackDate];
		if (requiredFields.some(value => !value.trim())) {
			setError({ code: 0, message: msg("completeAllRequiredFields") });
			return;
		}
		if (!Number.isFinite(amount) || amount <= 0) {
			setError({ code: 0, message: msg("chargebackAmountMustBeGreaterThan0") });
			return;
		}
		if (originalAmount !== undefined && (!Number.isFinite(originalAmount) || originalAmount <= 0)) {
			setError({ code: 0, message: msg("originalTransactionAmountMustBeGreaterThan0") });
			return;
		}
		if (feeAmount !== undefined && (!Number.isFinite(feeAmount) || feeAmount < 0)) {
			setError({ code: 0, message: msg("chargebackFeeCannotBeNegative") });
			return;
		}
		const impreciseIdentifier = [
			[msg("paymentId_d9c9afd8"), form.paymentId],
			[msg("dectaPaymentId"), form.dectaPaymentId],
			[msg("merchantOrderId"), form.merchantOrderId],
			["ARN", form.arn],
		].find(([, value]) => value && looksLikeScientificNotation(value));
		if (impreciseIdentifier) {
			setError({ code: 0, message: msg("templateMustContainTheExactIdentifierNotScientificNotation_b7755fff", { v0: impreciseIdentifier[0] }) });
			return;
		}
		if (form.cardLast4 && !/^\d{4}$/.test(form.cardLast4)) {
			setError({ code: 0, message: msg("cardLast4MustContainExactlyFourDigits") });
			return;
		}
		if (form.issuerCountry && !/^[a-z]{2,3}$/i.test(form.issuerCountry)) {
			setError({ code: 0, message: msg("issuerCountryMustBeA2Or3LetterIsoCountryCode") });
			return;
		}

		setBusy(true);
		setError(undefined);
		try {
			const response = await GD.REQ_API.request({
				method: "processing/statementchargeback",
				httpMethod: "POST",
				data: {
					action: "create",
					operation_type: "chargeback",
					payment_id: form.paymentId.trim(),
					decta_payment_id: form.dectaPaymentId.trim(),
					merchant_order_id: form.merchantOrderId.trim() || undefined,
					arn: form.arn.trim() || undefined,
					card_network: form.network,
					dispute_category: form.category,
					reason_code: form.reasonCode.trim(),
					chargeback_reference_number: form.referenceNumber.trim() || undefined,
					amount: Math.round(amount * 100),
					currency: form.currency,
					original_amount: originalAmount === undefined ? undefined : Math.round(originalAmount * 100),
					original_currency: originalAmount === undefined ? undefined : form.originalCurrency,
					chargeback_fee_amount: feeAmount === undefined ? undefined : Math.round(feeAmount * 100),
					chargeback_fee_currency: feeAmount === undefined ? undefined : form.feeCurrency,
					transaction_date: form.transactionDate,
					chargeback_date: form.chargebackDate,
					response_due_date: form.responseDueDate || undefined,
					status: form.processingStatus,
					dispute_status: form.disputeStatus,
					issuer_country: form.issuerCountry.trim().toUpperCase() || undefined,
					merchant_descriptor: form.descriptor.trim() || undefined,
					card_last_4: form.cardLast4 || undefined,
					rdr: form.rdr === "true",
					comments: form.comments.trim() || undefined,
					source: "manual",
				},
			});

			if (response.error) {
				setError(response.error);
				return;
			}

			notify.success(msg("chargebackCreated"));
			onCreated(form.chargebackDate);
		} catch (requestError) {
			const message = getNotificationErrorMessage(requestError, msg("chargebackCouldNotBeCreated"));
			setError({ code: 0, message });
			notify.error(message);
		} finally {
			setBusy(false);
		}
	};

	const formData = form as unknown as { [key: string]: string | number | boolean };

	return (
		<Panel
			busy={busy || parserBusy}
			decorator="absolute"
			label={msg("addChargeback")}
			onClose={close}
			growContent
			scrollable
			contentPadding="20px"
			sx={{ width: "92vw", maxWidth: "760px", maxHeight: "90vh", padding: 0 }}>
			<VBox gap="20px">
				<VBox gap="10px" sx={{ padding: "14px", border: "1px solid rgba(0,0,0,.1)", borderRadius: "8px", backgroundColor: "rgba(0,0,0,.018)" }}>
					<HBox gap="10px" sx={{ alignItems: "center" }}>
						<FileUp size={19} />
						<VBox gap="2px">
							<Typo sx={{ fontWeight: 600 }}>{msg("fillFromDocument")}</Typo>
							<Typo sx={{ color: "textSecondary", fontSize: "12px", lineHeight: "17px" }}>{msg("uploadACardChargebackExportParsedValuesRemainEditable")}</Typo>
						</VBox>
					</HBox>
					<FileSelect
						title={msg("chargebackDocument")}
						subtitle={msg("csvXlsxOrXlsMax10Mb")}
						accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
						disabled={busy || parserBusy}
						onChange={file => {
							setParserFile(file);
							setParserError(undefined);
							setParserMessage(undefined);
						}}
					/>
					{parserMessage && (
						<HBox sx={{ gap: "9px", alignItems: "flex-start", padding: "10px 12px", borderRadius: "7px", color: "#166534", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
							<Check size={17} />
							<Typo>{parserMessage}</Typo>
						</HBox>
					)}
					<ErrorBlock error={parserError} />
					<HBox sx={{ justifyContent: "flex-end" }}>
						<Button
							title={parserBusy ? msg("parsing") : msg("parseAndFillForm")}
							variant="secondary"
							icon={<WandSparkles size={16} />}
							disabled={!parserFile || busy || parserBusy}
							onClick={parseDocument}
						/>
					</HBox>
				</VBox>

				<VBox gap="4px">
					<Typo sx={{ fontWeight: 600 }}>{msg("disputeIdentification")}</Typo>
					<Typo sx={{ color: "textSecondary", fontSize: "12px", lineHeight: "18px" }}>{msg("useTheSchemeSpecificReasonCodeFromTheIssuerOrProcessorNotice_faf64999")}</Typo>
				</VBox>

				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px 16px" }}>
					<Input mandatory label={msg("paymentId_d9c9afd8")} name="paymentId" data={formData} description={msg("storedAsTextToPreserveLongIds")} onChange={value => updateField("paymentId", value)} />
					<Input mandatory label={msg("dectaPaymentId")} name="dectaPaymentId" data={formData} description={msg("storedAsTextToPreserveLongIds")} onChange={value => updateField("dectaPaymentId", value)} />
					<Input label={msg("merchantOrderId")} name="merchantOrderId" data={formData} onChange={value => updateField("merchantOrderId", value)} />
					<Input label="ARN" name="arn" data={formData} description={msg("enterTheExactValueScientificNotationIsRejected")} onChange={value => updateField("arn", value)} />
					<Select<TSelectOption> label={msg("cardNetwork")} name="network" data={formData} values={NETWORK_OPTIONS} onChange={option => updateNetwork(option.value)} />
					<Select<TSelectOption> label={msg("disputeCategory")} name="category" data={formData} values={CATEGORY_OPTIONS} onChange={option => updateField("category", option.value)} />
					<Input mandatory label={msg("reasonCode")} name="reasonCode" data={formData} placeholder={form.network === "visa" ? msg("eG104Or131") : msg("schemeReasonCode")} onChange={value => updateField("reasonCode", value)} />
					<Input label={msg("chargebackReference")} name="referenceNumber" data={formData} onChange={value => updateField("referenceNumber", value)} />
					<Input label={msg("issuerCountry")} name="issuerCountry" maxLength={3} data={formData} placeholder={msg("eGCyp")} onChange={value => updateField("issuerCountry", value)} />
					<Input label={msg("merchantDescriptor")} name="descriptor" data={formData} onChange={value => updateField("descriptor", value)} />
				</Box>

				<VBox gap="4px">
					<Typo sx={{ fontWeight: 600 }}>{msg("financialDetailsAndDates")}</Typo>
					<Typo sx={{ color: "textSecondary", fontSize: "12px", lineHeight: "18px" }}>{msg("theAmountMayBePartialAndCanDifferFromTheOriginalTransactionAmount")}</Typo>
				</VBox>

				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px 16px" }}>
					<Input mandatory formatAmount label={msg("chargebackAmount")} name="amount" data={formData} units={form.currency} placeholder="0.00" onChange={value => updateField("amount", value)} />
					<Select<TSelectOption> label={msg("currency_f3323700")} name="currency" data={formData} values={CURRENCY_OPTIONS} onChange={option => updateField("currency", option.value)} />
					<Input formatAmount label={msg("originalTransactionAmount")} name="originalAmount" data={formData} units={form.originalCurrency} placeholder="0.00" onChange={value => updateField("originalAmount", value)} />
					<Select<TSelectOption> label={msg("originalCurrency")} name="originalCurrency" data={formData} values={CURRENCY_OPTIONS} onChange={option => updateField("originalCurrency", option.value)} />
					<Input formatAmount label={msg("chargebackFee")} name="feeAmount" data={formData} units={form.feeCurrency} placeholder="0.00" onChange={value => updateField("feeAmount", value)} />
					<Select<TSelectOption> label={msg("feeCurrency")} name="feeCurrency" data={formData} values={CURRENCY_OPTIONS} onChange={option => updateField("feeCurrency", option.value)} />
					<DatePickerField timeZone="UTC" size="medium" label={msg("originalTransactionDate")} value={form.transactionDate} max={todayISO} onChange={value => updateField("transactionDate", value)} />
					<DatePickerField timeZone="UTC" size="medium" label={msg("chargebackReceivedDate")} value={form.chargebackDate} max={todayISO} onChange={value => updateField("chargebackDate", value)} />
					<DatePickerField timeZone="UTC" size="medium" label={msg("responseDueDate")} value={form.responseDueDate} allowClear onChange={value => updateField("responseDueDate", value)} />
					<Select<TSelectOption> label={msg("disputeStage")} name="disputeStatus" data={formData} values={DISPUTE_STATUS_OPTIONS} onChange={option => updateField("disputeStatus", option.value)} />
					<Select<TSelectOption> label={msg("recordProcessingStatus")} name="processingStatus" data={formData} values={PROCESSING_STATUS_OPTIONS} onChange={option => updateField("processingStatus", option.value)} />
					{form.network === "visa" && <Select<TSelectOption> label="Visa RDR" name="rdr" data={formData} values={YES_NO_OPTIONS} onChange={option => updateField("rdr", option.value)} />}
				</Box>

				<VBox gap="14px">
					<Input label={msg("cardLast4Optional")} name="cardLast4" type="number" maxLength={4} data={formData} description={msg("doNotEnterAFullCardNumber")} onChange={value => updateField("cardLast4", value)} />
					<Input label={msg("commentsCaseContext")} name="comments" type="textarea" multiline data={formData} placeholder={msg("issuerMessageCustomerClaimEvidenceNotes")} onChange={value => updateField("comments", value)} />
				</VBox>

				<ErrorBlock error={error} />

				<HBox gap="10px" sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
					<Button title={msg("cancel")} variant="secondary" disabled={busy || parserBusy} onClick={close} />
					<Button title={busy ? msg("creating") : msg("createChargeback")} variant="primary" disabled={busy || parserBusy} onClick={createChargeback} />
				</HBox>
			</VBox>
		</Panel>
	);
};

const useChargebackFile = (entry: IChargebackDayEntry) => {
	const key = entry.filePath;
	const [state, setState] = useState<{ key: string; text?: string; error?: string } | undefined>(undefined);

	useEffect(() => {
		const cached = readCache(fileCache, key);
		if (cached !== undefined) {
			setState({ key, text: cached });
			return;
		}

		let active = true;
		const loadFile = async () => {
			try {
				const dectaFile = await GD.REQ_API.request({
					method: "processing/statementfile",
					data: { path: key },
					responseType: "blob",
				});

				if (!dectaFile?.data) throw new Error(dectaFile?.error?.message || msg("theSourceFileCouldNotBeLoaded"));

				const text: string = await dectaFile.data.text();
				fileCache.set(key, { value: text, at: Date.now() });
				if (active) setState({ key, text });
			} catch (error) {
				if (active) setState({ key, error: error instanceof Error ? error.message : msg("theSourceFileCouldNotBeLoaded") });
			}
		};

		loadFile();
		return () => {
			active = false;
		};
	}, [key]);

	const current = state?.key === key ? state : undefined;
	const file = readCache(fileCache, key) ?? current?.text;
	const parsed = useMemo(() => (file === undefined ? undefined : parseChargebackFile(file)), [file]);
	const fileError = current?.error ?? (parsed && !parsed.parsed ? msg("theFileIsNotJsonSoChargebackDetailsCannotBeLinkedAutomatically") : undefined);

	return {
		file,
		fileData: parsed?.data,
		fileChargebacks: parsed?.chargebacks ?? [],
		fileBusy: file === undefined && !current?.error,
		fileError,
	};
};

const findFileChargeback = (fileChargebacks: Record<string, unknown>[], chargeback: IChargebackEntry) =>
	fileChargebacks.find(item => String(item.PAYMENT_ID ?? "") === String(chargeback.decta_payment_id));

const ChargebackDetailsLoader = ({ entry, chargeback, onClose }: { entry: IChargebackDayEntry; chargeback: IChargebackEntry; onClose: () => void }) => {
	const { fileChargebacks, fileBusy } = useChargebackFile(entry);
	return <ChargebackDetailsPopup chargeback={chargeback} fileData={findFileChargeback(fileChargebacks, chargeback)} fileBusy={fileBusy} onClose={onClose} />;
};

const fileNameOf = (path: string) => path.split(/[\\/]/).filter(Boolean).pop() ?? path;

const FileContentSkeleton = () => (
	<VBox gap="10px" sx={{ padding: "4px 0" }}>
		{[38, 62, 54, 70, 46, 66, 58, 34, 60, 50, 42, 28].map((width, index) => (
			<Shimmer key={index} $width={`${width}%`} $height="10px" style={{ marginLeft: index % 4 === 0 ? "0" : "18px" }} />
		))}
	</VBox>
);

const FileHeaderButton = ({ icon, title, disabled, onClick }: { icon: ReactNode; title: string; disabled?: boolean; onClick: () => void }) => (
	<Box
		onClick={disabled ? undefined : onClick}
		sx={{
			display: "flex",
			alignItems: "center",
			gap: "6px",
			height: "32px",
			padding: "0 12px",
			border: "1px solid #d0d5dd",
			borderRadius: "8px",
			backgroundColor: "#fff",
			boxShadow: "0 1px 2px rgba(16,24,40,.05)",
			color: COLORS.text,
			fontSize: "13px",
			fontWeight: 500,
			whiteSpace: "nowrap",
			cursor: disabled ? "default" : "pointer",
			opacity: disabled ? 0.45 : 1,
			userSelect: "none",
			transition: "background-color .15s",
			"&:hover": disabled ? {} : { backgroundColor: "#f9fafb" },
		}}>
		{icon}
		{title}
	</Box>
);

const ChargebackFilePopup = ({ entry, onClose }: { entry: IChargebackDayEntry; onClose: () => void }) => {
	useLocale();
	const { file, fileData, fileChargebacks, fileBusy, fileError } = useChargebackFile(entry);
	const [detailsId, setDetailsId] = useState<number | undefined>(undefined);
	const [downloadBusy, setDownloadBusy] = useState(false);

	useEffect(() => {
		setDetailsId(undefined);
	}, [entry]);

	const fileName = fileNameOf(entry.filePath);
	const detailsChargeback = entry.chargebacks.find(chargeback => chargeback.id === detailsId);
	const detailsFileData = detailsChargeback ? findFileChargeback(fileChargebacks, detailsChargeback) : undefined;
	const totalAmount = sumAmount(entry);
	const formattedFile = fileData === undefined ? file ?? "" : JSON.stringify(fileData, null, 2);
	const date = formatUtcDateParts(Number(entry.year), Number(entry.month), Number(entry.day));

	const download = async () => {
		if (downloadBusy) return;
		setDownloadBusy(true);
		try {
			await loadDectaFile(entry.filePath, fileName);
		} finally {
			setDownloadBusy(false);
		}
	};

	return (
		<>
			<Panel
				growContent
				disableScroll
				decorator="absolute"
				onClose={() => {
					if (detailsId !== undefined) {
						setDetailsId(undefined);
						return;
					}
					onClose();
				}}
				sx={{ padding: "0", width: "860px", maxWidth: "94vw", height: "82dvh", maxHeight: "88dvh", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
				<VBox sx={{ flex: 1, minHeight: 0, fontFamily: "'Euclid Circular B', sans-serif" }}>
					<VBox gap="10px" sx={{ flex: "0 0 auto", padding: "20px 24px 14px", backgroundColor: "#fff" }}>
						<HBox sx={{ alignItems: "flex-start", gap: "16px" }}>
							<VBox gap="5px" sx={{ flex: 1, minWidth: 0 }}>
								<Typo sx={{ fontSize: "11px", lineHeight: "16px", color: COLORS.secondary }}>{msg("cbFileEyebrow", { v0: date })}</Typo>
								<Typo sx={{ fontSize: "21px", lineHeight: "28px", fontWeight: 600, color: COLORS.title, overflowWrap: "anywhere" }}>{fileName}</Typo>
							</VBox>
							<Box
								onClick={onClose}
								title={msg("cbClose")}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
									width: "32px",
									height: "32px",
									borderRadius: "7px",
									color: COLORS.secondary,
									cursor: "pointer",
									"&:hover": { backgroundColor: "#f2f4f7", color: COLORS.title },
								}}>
								<X size={18} />
							</Box>
						</HBox>

						<HBox sx={{ alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
							<Typo sx={{ fontSize: "12px", lineHeight: "18px", color: COLORS.secondary }}>
								{msg("chargebacks_4b3fbb67")}
								{entry.count} · {cents(totalAmount)} EUR
							</Typo>
							<Box sx={{ minWidth: 0, fontSize: "11px", lineHeight: "18px", color: COLORS.muted, overflowWrap: "anywhere" }}>{entry.filePath}</Box>
							<Spacer grow />
							<FileHeaderButton icon={<Download size={15} />} title={msg("cbDownload")} disabled={downloadBusy} onClick={download} />
						</HBox>
					</VBox>

					<VBox sx={{ flex: 1, minHeight: 0 }}>
						<Tabs variant="modern" scrollable spaceBottom={0} sx={{ padding: "0 24px", backgroundColor: "#fff" }}>
							<TabItem title={msg("cbFileContent")} section={0} icon={<FileJson size={16} />}>
								<VBox gap="14px" sx={{ flex: 1, padding: "20px 24px 24px", backgroundColor: "#f5f7fa" }}>
									{fileError && <DetailsNotice icon={<AlertCircle size={18} color={COLORS.amber} />} title={fileError} />}
									{/* Pre brings its own dark panel and copy button, so it sits on the tab background without an extra card. */}
									{fileBusy ? (
										<Box sx={{ ...CARD_SX, borderRadius: "10px", padding: "18px" }}>
											<FileContentSkeleton />
										</Box>
									) : formattedFile ? (
										<Box sx={{ minWidth: 0, fontSize: "12px", lineHeight: "18px" }}>
											<Pre data={formattedFile} wrap />
										</Box>
									) : (
										<Typo sx={{ fontSize: "13px", color: COLORS.secondary }}>{msg("noFileContentAvailable")}</Typo>
									)}
								</VBox>
							</TabItem>

							<TabItem title={`${msg("cbKpiChargebacks")} · ${entry.count}`} section={1} icon={<List size={16} />}>
								<VBox gap="14px" sx={{ flex: 1, padding: "20px 24px 24px", backgroundColor: "#f5f7fa" }}>
									<Box sx={{ ...CARD_SX, borderRadius: "10px", overflow: "hidden", padding: "4px 0" }}>
										{entry.chargebacks.map(chargeback => (
											<DayChargebackRow key={chargeback.id} chargeback={chargeback} onClick={() => setDetailsId(chargeback.id)} />
										))}
									</Box>
								</VBox>
							</TabItem>
						</Tabs>
					</VBox>
				</VBox>
			</Panel>

			{detailsChargeback && (
				<ChargebackDetailsPopup chargeback={detailsChargeback} fileData={detailsFileData} fileBusy={fileBusy} onClose={() => setDetailsId(undefined)} />
			)}
		</>
	);
};

type TChargebackFileParseResult = {
	data: unknown;
	chargebacks: Record<string, unknown>[];
	parsed: boolean;
};

const parseChargebackFile = (text: string): TChargebackFileParseResult => {
	let data: unknown = text;
	let parsed = false;

	for (let depth = 0; depth < 4 && typeof data === "string"; depth++) {
		try {
			data = JSON.parse(data);
			parsed = true;
		} catch {
			break;
		}
	}

	const rows = Array.isArray(data)
		? data
		: data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)
			? (data as { data: unknown[] }).data
			: [];
	const chargebacks = rows.filter(
		(row): row is Record<string, unknown> => Boolean(row && typeof row === "object" && !Array.isArray(row) && (row as Record<string, unknown>).PAYMENT_ID),
	);

	return { data, chargebacks, parsed };
};

const DETAIL_FIELDS = [
	"CARD",
	"PAYMENT_ID",
	"CHB_AMOUNT",
	"CHB_CCY",
	"CHB_SETTL_AMOUNT",
	"CHB_SETTL_CCY",
	"REASON_CODE",
	"FUNCTION_CODE_NAME",
	"CHB_REFER_NUM",
	"CHB_TR_DATE",
	"CHB_PROC_DATE",
	"TR_AMOUNT",
	"TR_CCY",
	"TR_DATE_TIME",
	"MERCHANT_NAME",
	"MERCHANT_ID",
	"MCC_CODE",
	"ISSUER_COUNTRY",
	"COMMENTS",
] as const;

const FIELD_LABELS: Record<string, string> = {
	CARD: "Card",
	get PAYMENT_ID() { return msg("dectaPaymentId"); },
	get CHB_AMOUNT() { return msg("chargebackAmount"); },
	get CHB_CCY() { return msg("chargebackCurrency"); },
	get CHB_SETTL_AMOUNT() { return msg("chargebackSettlementAmount"); },
	get CHB_SETTL_CCY() { return msg("chargebackSettlementCurrency"); },
	get REASON_CODE() { return msg("reasonCode"); },
	FUNCTION_CODE_NAME: "Function",
	get CHB_REFER_NUM() { return msg("referenceNumber"); },
	get CHB_TR_DATE() { return msg("chargebackTransactionDate"); },
	get CHB_PROC_DATE() { return msg("processingDate"); },
	get TR_AMOUNT() { return msg("transactionAmount"); },
	get TR_CCY() { return msg("transactionCurrency"); },
	get TR_DATE_TIME() { return msg("transactionDateTime"); },
	MERCHANT_NAME: "Merchant",
	get MERCHANT_ID() { return msg("merchantId_656646fc"); },
	MCC_CODE: "MCC",
	get ISSUER_COUNTRY() { return msg("issuerCountry"); },
	COMMENTS: "Comments",
};

type TFileField = { key: string; currencyKey?: string; copy?: boolean; mono?: boolean };

// Chargeback file fields grouped the way people read a dispute: what was disputed, the original payment, who it belongs to.
const FILE_FIELD_GROUPS: { title: () => string; fields: TFileField[] }[] = [
	{
		title: () => msg("cbDetailsDispute"),
		fields: [
			{ key: "REASON_CODE" },
			{ key: "FUNCTION_CODE_NAME" },
			{ key: "CHB_REFER_NUM", copy: true },
			{ key: "CHB_AMOUNT", currencyKey: "CHB_CCY" },
			{ key: "CHB_SETTL_AMOUNT", currencyKey: "CHB_SETTL_CCY" },
			{ key: "CHB_TR_DATE" },
			{ key: "CHB_PROC_DATE" },
		],
	},
	{
		title: () => msg("cbDetailsTransaction"),
		fields: [{ key: "TR_AMOUNT", currencyKey: "TR_CCY" }, { key: "TR_DATE_TIME" }, { key: "CARD" }, { key: "ISSUER_COUNTRY" }],
	},
	{
		title: () => msg("cbDetailsMerchant"),
		fields: [{ key: "MERCHANT_NAME" }, { key: "MERCHANT_ID", copy: true }, { key: "MCC_CODE" }],
	},
];

const hasFileValue = (value: unknown) => value !== "" && value != null;

const STATUS_TONES: Record<string, { color: string; background: string; border: string }> = {
	green: { color: "#067647", background: "#ecfdf3", border: "#abefc6" },
	amber: { color: "#b54708", background: "#fffaeb", border: "#fedf89" },
	red: { color: "#b42318", background: "#fef3f2", border: "#fecdca" },
	gray: { color: "#344054", background: "#f9fafb", border: "#d0d5dd" },
};

const STATUS_LABELS: Record<string, () => string> = {
	opened: () => msg("cbStatusOpened"),
	pending: () => msg("cbStatusPending"),
	completed: () => msg("cbStatusCompleted"),
	closed: () => msg("cbStatusClosed"),
	failed: () => msg("cbStatusFailed"),
	error: () => msg("cbStatusFailed"),
};

export const chargebackStatusLabel = (status: string) => STATUS_LABELS[status.toLowerCase()]?.() ?? capitalize(status.replaceAll("_", " "));

export const statusTone = (status: string) => {
	const value = status.toLowerCase();
	if (["success", "accepted", "won", "closed"].includes(value)) return STATUS_TONES.green;
	if (["failed", "error", "lost", "declined"].includes(value)) return STATUS_TONES.red;
	if (["pending", "received", "under_review", "representment", "new", "opened"].includes(value)) return STATUS_TONES.amber;
	return STATUS_TONES.gray;
};

type TReasonText = { title: () => string; about: () => string; evidence: () => string };

// Visa dispute conditions as described in "Visa Dispute Management Guide for Merchants" (October 2026, Visa Public).
const VISA_REASONS: Record<string, TReasonText> = {
	"10.1": { title: () => msg("cbVisa10_1Title"), about: () => msg("cbVisa10_1About"), evidence: () => msg("cbVisa10_1Evidence") },
	"10.2": { title: () => msg("cbVisa10_2Title"), about: () => msg("cbVisa10_2About"), evidence: () => msg("cbVisa10_2Evidence") },
	"10.3": { title: () => msg("cbVisa10_3Title"), about: () => msg("cbVisa10_3About"), evidence: () => msg("cbVisa10_3Evidence") },
	"10.4": { title: () => msg("cbVisa10_4Title"), about: () => msg("cbVisa10_4About"), evidence: () => msg("cbVisa10_4Evidence") },
	"10.5": { title: () => msg("cbVisa10_5Title"), about: () => msg("cbVisa10_5About"), evidence: () => msg("cbVisa10_5Evidence") },
	"11.1": { title: () => msg("cbVisa11_1Title"), about: () => msg("cbVisa11_1About"), evidence: () => msg("cbVisa11_1Evidence") },
	"11.2": { title: () => msg("cbVisa11_2Title"), about: () => msg("cbVisa11_2About"), evidence: () => msg("cbVisa11_2Evidence") },
	"11.3": { title: () => msg("cbVisa11_3Title"), about: () => msg("cbVisa11_3About"), evidence: () => msg("cbVisa11_3Evidence") },
	"12.2": { title: () => msg("cbVisa12_2Title"), about: () => msg("cbVisa12_2About"), evidence: () => msg("cbVisa12_2Evidence") },
	"12.3": { title: () => msg("cbVisa12_3Title"), about: () => msg("cbVisa12_3About"), evidence: () => msg("cbVisa12_3Evidence") },
	"12.4": { title: () => msg("cbVisa12_4Title"), about: () => msg("cbVisa12_4About"), evidence: () => msg("cbVisa12_4Evidence") },
	"12.5": { title: () => msg("cbVisa12_5Title"), about: () => msg("cbVisa12_5About"), evidence: () => msg("cbVisa12_5Evidence") },
	"12.6": { title: () => msg("cbVisa12_6Title"), about: () => msg("cbVisa12_6About"), evidence: () => msg("cbVisa12_6Evidence") },
	"12.7": { title: () => msg("cbVisa12_7Title"), about: () => msg("cbVisa12_7About"), evidence: () => msg("cbVisa12_7Evidence") },
	"13.1": { title: () => msg("cbVisa13_1Title"), about: () => msg("cbVisa13_1About"), evidence: () => msg("cbVisa13_1Evidence") },
	"13.2": { title: () => msg("cbVisa13_2Title"), about: () => msg("cbVisa13_2About"), evidence: () => msg("cbVisa13_2Evidence") },
	"13.3": { title: () => msg("cbVisa13_3Title"), about: () => msg("cbVisa13_3About"), evidence: () => msg("cbVisa13_3Evidence") },
	"13.4": { title: () => msg("cbVisa13_4Title"), about: () => msg("cbVisa13_4About"), evidence: () => msg("cbVisa13_4Evidence") },
	"13.5": { title: () => msg("cbVisa13_5Title"), about: () => msg("cbVisa13_5About"), evidence: () => msg("cbVisa13_5Evidence") },
	"13.6": { title: () => msg("cbVisa13_6Title"), about: () => msg("cbVisa13_6About"), evidence: () => msg("cbVisa13_6Evidence") },
	"13.7": { title: () => msg("cbVisa13_7Title"), about: () => msg("cbVisa13_7About"), evidence: () => msg("cbVisa13_7Evidence") },
	"13.8": { title: () => msg("cbVisa13_8Title"), about: () => msg("cbVisa13_8About"), evidence: () => msg("cbVisa13_8Evidence") },
	"13.9": { title: () => msg("cbVisa13_9Title"), about: () => msg("cbVisa13_9About"), evidence: () => msg("cbVisa13_9Evidence") },
};

type TReasonCategory = { label: () => string; tone: { color: string; background: string; border: string } };

const VISA_CATEGORIES: Record<string, TReasonCategory> = {
	"10": { label: () => msg("cbVisaCategory10"), tone: STATUS_TONES.red },
	"11": { label: () => msg("cbVisaCategory11"), tone: STATUS_TONES.amber },
	"12": { label: () => msg("cbVisaCategory12"), tone: STATUS_TONES.gray },
	"13": { label: () => msg("cbVisaCategory13"), tone: { color: "#175cd3", background: "#eff8ff", border: "#b2ddff" } },
};

// Mastercard message reason codes as described in the Mastercard "Chargeback Guide" (13 December 2018 edition).
// The four groups match "Chargebacks Made Simple Guide" (July 2025). Legacy codes (4807, 4812, 4831, 4841, 4842, 4846,
// 4855, 4859, 4860) are still accepted by Mastercard but fold into the four groups.
const MASTERCARD_REASONS: Record<string, TReasonText> = {
	"4807": { title: () => msg("cbMc4807Title"), about: () => msg("cbMc4807About"), evidence: () => msg("cbMc4807Evidence") },
	"4808": { title: () => msg("cbMc4808Title"), about: () => msg("cbMc4808About"), evidence: () => msg("cbMc4808Evidence") },
	"4812": { title: () => msg("cbMc4812Title"), about: () => msg("cbMc4812About"), evidence: () => msg("cbMc4812Evidence") },
	"4831": { title: () => msg("cbMc4831Title"), about: () => msg("cbMc4831About"), evidence: () => msg("cbMc4831Evidence") },
	"4834": { title: () => msg("cbMc4834Title"), about: () => msg("cbMc4834About"), evidence: () => msg("cbMc4834Evidence") },
	"4837": { title: () => msg("cbMc4837Title"), about: () => msg("cbMc4837About"), evidence: () => msg("cbMc4837Evidence") },
	"4840": { title: () => msg("cbMc4840Title"), about: () => msg("cbMc4840About"), evidence: () => msg("cbMc4840Evidence") },
	"4841": { title: () => msg("cbMc4841Title"), about: () => msg("cbMc4841About"), evidence: () => msg("cbMc4841Evidence") },
	"4842": { title: () => msg("cbMc4842Title"), about: () => msg("cbMc4842About"), evidence: () => msg("cbMc4842Evidence") },
	"4846": { title: () => msg("cbMc4846Title"), about: () => msg("cbMc4846About"), evidence: () => msg("cbMc4846Evidence") },
	"4849": { title: () => msg("cbMc4849Title"), about: () => msg("cbMc4849About"), evidence: () => msg("cbMc4849Evidence") },
	"4850": { title: () => msg("cbMc4850Title"), about: () => msg("cbMc4850About"), evidence: () => msg("cbMc4850Evidence") },
	"4853": { title: () => msg("cbMc4853Title"), about: () => msg("cbMc4853About"), evidence: () => msg("cbMc4853Evidence") },
	"4854": { title: () => msg("cbMc4854Title"), about: () => msg("cbMc4854About"), evidence: () => msg("cbMc4854Evidence") },
	"4855": { title: () => msg("cbMc4855Title"), about: () => msg("cbMc4855About"), evidence: () => msg("cbMc4855Evidence") },
	"4859": { title: () => msg("cbMc4859Title"), about: () => msg("cbMc4859About"), evidence: () => msg("cbMc4859Evidence") },
	"4860": { title: () => msg("cbMc4860Title"), about: () => msg("cbMc4860About"), evidence: () => msg("cbMc4860Evidence") },
	"4863": { title: () => msg("cbMc4863Title"), about: () => msg("cbMc4863About"), evidence: () => msg("cbMc4863Evidence") },
	"4870": { title: () => msg("cbMc4870Title"), about: () => msg("cbMc4870About"), evidence: () => msg("cbMc4870Evidence") },
	"4871": { title: () => msg("cbMc4871Title"), about: () => msg("cbMc4871About"), evidence: () => msg("cbMc4871Evidence") },
	"4999": { title: () => msg("cbMc4999Title"), about: () => msg("cbMc4999About"), evidence: () => msg("cbMc4999Evidence") },
};

const MASTERCARD_GROUPS: Record<string, string> = {
	"4807": "auth",
	"4808": "auth",
	"4812": "auth",
	"4831": "poi",
	"4834": "poi",
	"4842": "poi",
	"4846": "poi",
	"4837": "fraud",
	"4840": "fraud",
	"4849": "fraud",
	"4863": "fraud",
	"4870": "fraud",
	"4871": "fraud",
	"4841": "dispute",
	"4853": "dispute",
	"4855": "dispute",
	"4859": "dispute",
	"4860": "dispute",
	"4850": "domestic",
	"4854": "domestic",
	"4999": "domestic",
};

const MASTERCARD_CATEGORIES: Record<string, TReasonCategory> = {
	auth: { label: () => msg("cbVisaCategory11"), tone: STATUS_TONES.amber },
	dispute: { label: () => msg("cbVisaCategory13"), tone: VISA_CATEGORIES["13"].tone },
	fraud: { label: () => msg("cbVisaCategory10"), tone: STATUS_TONES.red },
	poi: { label: () => msg("cbMcCategoryPoi"), tone: STATUS_TONES.gray },
	domestic: { label: () => msg("cbMcCategoryDomestic"), tone: STATUS_TONES.gray },
};

/**
 * Reason for a Decta chargeback record. Visa: "13.1", sub-codes like "12.6.1" fold into their condition.
 * Mastercard: four-digit message reason codes like "4853". A card of the other network hides the explanation.
 */
const resolveChargebackReason = (fileData?: Record<string, unknown>) => {
	if (!fileData || !hasFileValue(fileData.REASON_CODE)) return undefined;
	const code = String(fileData.REASON_CODE).trim();
	const brand = resolveCardBrand(fileData);
	const visa = code.match(/^(1[0-3])\.(\d)(?:\.\d)?$/);
	if (visa) {
		if (brand === "mastercard") return undefined;
		const reason = VISA_REASONS[`${visa[1]}.${visa[2]}`];
		return reason ? { network: "Visa", code, category: VISA_CATEGORIES[visa[1]], source: () => msg("cbReasonSource"), ...reason } : undefined;
	}
	if (brand === "visa") return undefined;
	const reason = MASTERCARD_REASONS[code];
	return reason ? { network: "Mastercard", code, category: MASTERCARD_CATEGORIES[MASTERCARD_GROUPS[code]], source: () => msg("cbReasonSourceMastercard"), ...reason } : undefined;
};

/** Reason title, group and card brand for a scheme reason code, e.g. ("4853", "MC") from chargebacks/analytics. */
export const describeChargebackReason = (reasonCode: string, cardType?: string) => {
	const record = { REASON_CODE: reasonCode, CARD_TYPE: cardType ?? "" };
	return { reason: resolveChargebackReason(record), brand: resolveCardBrand(record) };
};

/** Brand of a masked card such as "5353...6752". */
export const cardBrandOfMask = (card: string) => resolveCardBrand({ CARD: card });

const CATEGORY_GROUPS: Record<string, string> = { fraud: "10", authorization: "11", processing_error: "12", consumer_dispute: "13" };

/** Label and tone for a dispute category as the API names it ("consumer_dispute", "fraud"...). */
export const chargebackCategory = (category: string) => {
	const group = VISA_CATEGORIES[CATEGORY_GROUPS[category]];
	return group ? { label: group.label(), tone: group.tone } : { label: capitalize(category.replaceAll("_", " ")), tone: STATUS_TONES.gray };
};

const ReasonExplainer = ({ reason }: { reason: NonNullable<ReturnType<typeof resolveChargebackReason>> }) => {
	useLocale();
	return (
		<VBox gap="10px">
			<DetailsSectionLabel>{msg("cbReasonExplainer")}</DetailsSectionLabel>
			<VBox
				gap="12px"
				sx={{
					padding: "16px 18px",
					backgroundColor: "#fff",
					border: "1px solid #d0d5dd",
					borderRadius: "10px",
					boxShadow: "0 1px 2px rgba(16,24,40,.05)",
				}}>
				<VBox gap="6px">
					<HBox sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
						<Box
							sx={{
								padding: "2px 8px",
								borderRadius: "6px",
								border: `1px solid ${reason.category.tone.border}`,
								backgroundColor: reason.category.tone.background,
								color: reason.category.tone.color,
								fontSize: "11px",
								lineHeight: "16px",
								fontWeight: 600,
							}}>
							{reason.category.label()}
						</Box>
						<Typo sx={{ fontSize: "12px", lineHeight: "18px", color: "#98a2b3" }}>{reason.network} {reason.code}</Typo>
					</HBox>
					<Typo sx={{ fontSize: "15px", lineHeight: "22px", fontWeight: 600, color: "#182230" }}>{reason.title()}</Typo>
					<Typo sx={{ fontSize: "13px", lineHeight: "20px", color: "#344054" }}>{reason.about()}</Typo>
				</VBox>
				<VBox gap="4px" sx={{ paddingTop: "12px", borderTop: "1px solid #eaecf0" }}>
					<Typo sx={{ fontSize: "11px", lineHeight: "16px", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#667085" }}>
						{msg("cbReasonEvidence")}
					</Typo>
					<Typo sx={{ fontSize: "13px", lineHeight: "20px", color: "#344054" }}>{reason.evidence()}</Typo>
				</VBox>
				<Typo sx={{ fontSize: "11px", lineHeight: "16px", color: "#98a2b3" }}>{reason.source()}</Typo>
			</VBox>
		</VBox>
	);
};

const DetailsSectionLabel = ({ children }: { children: ReactNode }) => (
	<Typo sx={{ fontSize: "11px", lineHeight: "16px", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#667085" }}>{children}</Typo>
);

const DetailsCard = ({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) => (
	<VBox gap="10px">
		{aside ? (
			<HBox sx={{ alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
				<DetailsSectionLabel>{title}</DetailsSectionLabel>
				{aside}
			</HBox>
		) : (
			<DetailsSectionLabel>{title}</DetailsSectionLabel>
		)}
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "180px minmax(0, 1fr)",
				alignItems: "baseline",
				gap: "4px 16px",
				padding: "16px 18px",
				backgroundColor: "#fff",
				border: "1px solid #d0d5dd",
				borderRadius: "10px",
				boxShadow: "0 1px 2px rgba(16,24,40,.05)",
				"@media (max-width: 600px)": { gridTemplateColumns: "minmax(0, 1fr)" },
			}}>
			{children}
		</Box>
	</VBox>
);

const DetailsRow = ({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) => (
	<>
		<Box sx={{ fontSize: "12px", lineHeight: "26px", color: "#667085" }}>{label}</Box>
		<Box sx={{ fontSize: "13px", lineHeight: "26px", color: "#182230", minWidth: 0, overflowWrap: "anywhere", fontFamily: mono ? "monospace" : undefined }}>{children}</Box>
	</>
);

/** Mirrors the file detail cards while the file loads, so the layout does not jump when details arrive. */
const DetailsSkeleton = ({ title }: { title: string }) => (
	<VBox gap="22px" title={title}>
		{[5, 3].map((rows, card) => (
			<VBox key={card} gap="10px">
				<Shimmer $width="88px" $height="10px" />
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "180px minmax(0, 1fr)",
						alignItems: "center",
						gap: "14px 16px",
						padding: "18px",
						backgroundColor: "#fff",
						border: "1px solid #d0d5dd",
						borderRadius: "10px",
						boxShadow: "0 1px 2px rgba(16,24,40,.05)",
					}}>
					{Array.from({ length: rows }, (_, row) => (
						<Fragment key={row}>
							<Shimmer $width={`${50 + ((row * 17) % 35)}%`} $height="10px" />
							<Shimmer $width={`${30 + ((row * 23) % 45)}%`} $height="12px" />
						</Fragment>
					))}
				</Box>
			</VBox>
		))}
	</VBox>
);

const DetailsNotice = ({ icon, title, text }: { icon: ReactNode; title: string; text?: ReactNode }) => (
	<HBox
		sx={{
			gap: "12px",
			alignItems: "flex-start",
			padding: "16px 18px",
			backgroundColor: "#fff",
			border: "1px solid #d0d5dd",
			borderRadius: "10px",
			boxShadow: "0 1px 2px rgba(16,24,40,.05)",
			color: "#667085",
		}}>
		<Box sx={{ flexShrink: 0, paddingTop: "2px" }}>{icon}</Box>
		<VBox gap="2px" sx={{ minWidth: 0 }}>
			<Typo sx={{ fontSize: "13px", lineHeight: "20px", fontWeight: 600, color: "#182230" }}>{title}</Typo>
			{text && <Typo sx={{ fontSize: "12px", lineHeight: "18px", color: "#667085" }}>{text}</Typo>}
		</VBox>
	</HBox>
);

/** Brand from the BIN of the masked card number; without a known BIN, from Decta CARD_TYPE ("MC", "VISA", "Maestro"...). */
const resolveCardBrand = (fileData?: Record<string, unknown>): TCardBrand | undefined => {
	if (!fileData) return undefined;
	const digits = hasFileValue(fileData.CARD) ? String(fileData.CARD).trim().match(/^\d+/)?.[0] ?? "" : "";
	if (digits.startsWith("4")) return "visa";
	const bin = Number(digits.slice(0, 4));
	if (/^5[1-5]/.test(digits) || (bin >= 2221 && bin <= 2720)) return "mastercard";
	const cardType = hasFileValue(fileData.CARD_TYPE) ? String(fileData.CARD_TYPE).trim() : "";
	if (/visa|^vi?$/i.test(cardType)) return "visa";
	if (/master|maestro|cirrus|^mc/i.test(cardType)) return "mastercard";
	return undefined;
};

// Visa is a wide wordmark, Mastercard two circles: these heights give them a similar visual weight.
export const BRAND_MARK_HEIGHT: Record<TCardBrand, { row: number; header: number }> = { visa: { row: 9, header: 8 }, mastercard: { row: 13, header: 12 } };
const BRAND_NAMES: Record<TCardBrand, string> = { visa: "Visa", mastercard: "Mastercard" };

/** Decta ISSUER_COUNTRY is ISO alpha-3 ("FRA"); alpha-2 values are accepted too. */
const resolveCountry = (value: unknown) => {
	if (!hasFileValue(value)) return undefined;
	const code = String(value).trim().toUpperCase();
	const country = CountriesData.countries.find(item => item.alpha3 === code || item.alpha2 === code);
	return country ? { code, alpha2: country.alpha2, name: countryName(country.alpha2, country.name) } : undefined;
};

const CountryFlagImage = ({ alpha2, height }: { alpha2: string; height: number }) => (
	<img
		src={`https://flagcdn.com/${alpha2.toLowerCase()}.svg`}
		alt=""
		loading="lazy"
		style={{ display: "block", height: `${height}px`, width: `${Math.round(height * 1.5)}px`, objectFit: "cover", borderRadius: "3px", boxShadow: "0 0 0 1px rgba(16,24,40,.12)", flexShrink: 0 }}
	/>
);

const CountryValue = ({ value }: { value: unknown }) => {
	const country = resolveCountry(value);
	if (!country) return <>{String(value)}</>;
	return (
		<HBox sx={{ alignItems: "center", gap: "8px", minHeight: "26px" }}>
			<CountryFlagImage alpha2={country.alpha2} height={14} />
			<span>
				{country.name} <span style={{ color: "#98a2b3" }}>{country.code}</span>
			</span>
		</HBox>
	);
};

/** "413318%1193" → "4133 18•• •••• 1193". A single mask character stands for all hidden digits of a 16-digit PAN. */
const formatCardMask = (value: string) => {
	const match = value.trim().match(/^(\d{4,8})([^\d\s]+)(\d{2,4})$/);
	if (!match) return value;
	const [, first, mask, last] = match;
	const hidden = mask.length > 1 ? mask.length : Math.max(16 - first.length - last.length, 1);
	return `${first}${"•".repeat(hidden)}${last}`.replace(/(.{4})(?=.)/g, "$1 ");
};

const CardValue = ({ fileData }: { fileData: Record<string, unknown> }) => {
	const brand = resolveCardBrand(fileData);
	return (
		<HBox sx={{ alignItems: "center", gap: "8px", minHeight: "26px" }}>
			{brand && <CardBrandMark brand={brand} height={BRAND_MARK_HEIGHT[brand].row} />}
			<Box sx={{ fontVariantNumeric: "tabular-nums", letterSpacing: ".02em", whiteSpace: "nowrap" }}>{formatCardMask(String(fileData.CARD))}</Box>
		</HBox>
	);
};

const fileFieldValue = (fileData: Record<string, unknown>, field: TFileField): ReactNode => {
	const value = fileData[field.key];
	if (field.key === "CARD") return <CardValue fileData={fileData} />;
	if (field.key === "ISSUER_COUNTRY") return <CountryValue value={value} />;
	if (field.copy && (typeof value === "string" || typeof value === "number")) return <CopyableValue value={value} />;
	const currency = field.currencyKey && hasFileValue(fileData[field.currencyKey]) ? ` ${String(fileData[field.currencyKey])}` : "";
	return `${String(formatFileValue(field.key, value))}${currency}`;
};

type TSessionField = { key: string; label: () => string; copy?: boolean; mono?: boolean; format?: (session: Record<string, unknown>) => string | undefined };

const joinValues = (...values: unknown[]) => values.filter(hasFileValue).map(String).join(" · ") || undefined;

// raw.session of chargebacks/get: the original payment as processing sees it.
const SESSION_FIELDS: TSessionField[] = [
	{ key: "ssid", label: () => msg("cbSessionOriginal"), copy: true },
	{
		key: "amount",
		label: () => msg("transactionAmount"),
		format: session => (typeof session.amount === "number" ? `${cents(session.amount)}${hasFileValue(session.currency) ? ` ${String(session.currency)}` : ""}` : undefined),
	},
	{ key: "status", label: () => msg("cbSessionStatus"), format: session => joinValues(session.status, session.substatus) },
	{ key: "settlement_status", label: () => msg("cbSessionSettlement"), format: session => joinValues(session.settlement_status, hasFileValue(session.settlement_date) ? formatDateTime(String(session.settlement_date)) : undefined) },
	{ key: "antifraud_status", label: () => msg("cbSessionAntifraud") },
	{ key: "arn", label: () => "ARN", copy: true },
	{ key: "rrn", label: () => "RRN", copy: true },
	{ key: "processing_mid", label: () => msg("cbSessionProcessingMid"), copy: true },
	{ key: "external_payment_id", label: () => msg("cbSessionExternalPaymentId"), copy: true },
	{ key: "external_merchant_id", label: () => msg("cbSessionExternalMerchantId"), copy: true },
	{ key: "acquirer_id", label: () => msg("cbSessionAcquirerId") },
	{ key: "related_transaction_id", label: () => msg("cbSessionRelatedTransaction"), copy: true },
];

const sessionValue = (session: Record<string, unknown>, field: TSessionField): ReactNode => {
	if (field.format) return field.format(session);
	const value = session[field.key];
	// 0 means "none" for numeric ids here.
	if (!hasFileValue(value) || value === 0) return undefined;
	if (field.copy && (typeof value === "string" || typeof value === "number")) return <CopyableValue value={value} />;
	return typeof value === "string" ? capitalize(value.replaceAll("_", " ")) : String(value);
};

const SessionDetails = ({ session }: { session: Record<string, unknown> }) => {
	useLocale();
	const rows = SESSION_FIELDS.map(field => ({ field, value: sessionValue(session, field) })).filter(row => row.value != null && row.value !== "");
	if (!rows.length) return null;
	return (
		<DetailsCard title={msg("cbSessionTitle")}>
			{rows.map(({ field, value }) => (
				<DetailsRow key={field.key} label={field.label()} mono={field.mono}>
					{value}
				</DetailsRow>
			))}
		</DetailsCard>
	);
};

export interface IChargebackPopupEntry {
	id: number;
	amount: number;
	currency?: string;
	status?: string;
	created_at?: string | number;
	chargeback_date?: string;
	payment_id?: string | number;
	decta_payment_id?: string | number;
	session_id?: string | number;
	merchant_id?: string | number;
}

export const ChargebackDetailsPopup = ({
	chargeback,
	fileData,
	fileBusy,
	merchantName,
	showFileDetails = true,
	session,
	recordError,
	raw,
	showResend = true,
	onOpenTransaction,
	onClose,
}: {
	chargeback: IChargebackPopupEntry;
	fileData?: Record<string, unknown>;
	fileBusy?: boolean;
	merchantName?: string;
	showFileDetails?: boolean;
	/** Original payment from processing (chargebacks/get → raw.session). */
	session?: Record<string, unknown>;
	/** The chargeback record could not be loaded. */
	recordError?: string;
	/** Shown as is in the Raw tab; defaults to the Decta record (fileData). */
	raw?: unknown;
	showResend?: boolean;
	/** Overrides the default Decta session popup, e.g. to open the client's transaction preview. */
	onOpenTransaction?: () => void;
	onClose: () => void;
}) => {
	useLocale();
	const [resendBusy, setResendBusy] = useState(false);
	// The label follows session_id first, so the value must too; payment_id then gets its own row.
	const transactionId = chargeback.session_id ?? chargeback.payment_id;
	const extraPaymentId = chargeback.session_id != null && chargeback.payment_id != null && chargeback.payment_id !== "" ? chargeback.payment_id : undefined;
	const transactionLabel = chargeback.session_id != null ? msg("sessionId") : chargeback.payment_id != null ? msg("paymentId_d9c9afd8") : "ID";
	const tone = chargeback.status ? statusTone(chargeback.status) : undefined;
	const reasonCode = fileData && hasFileValue(fileData.REASON_CODE) ? String(fileData.REASON_CODE) : undefined;
	const reason = resolveChargebackReason(fileData);
	const cardBrand = resolveCardBrand(fileData);
	const overviewDate = chargeback.chargeback_date
		? { label: msg("cbDetailsChargebackDate"), value: chargeback.chargeback_date }
		: chargeback.created_at != null && chargeback.created_at !== ""
			? { label: displayField("created_at"), value: String(chargeback.created_at) }
			: undefined;
	const groups = fileData
		? FILE_FIELD_GROUPS.map(group => ({ title: group.title(), fields: group.fields.filter(field => hasFileValue(fileData[field.key])) })).filter(group => group.fields.length)
		: [];
	const comments = fileData && hasFileValue(fileData.COMMENTS) ? String(fileData.COMMENTS) : undefined;
	const additionalFields = fileData
		? Object.entries(fileData).filter(([key, value]) => !DETAIL_FIELDS.includes(key as (typeof DETAIL_FIELDS)[number]) && hasFileValue(value))
		: [];

	const resendWebhook = async () => {
		if (resendBusy) return;

		setResendBusy(true);
		try {
			const response = await GD.REQ_API.request({
				method: "processing/chargeback/resend",
				httpMethod: "POST",
				data: { id: chargeback.id },
			});

			if (response?.error) {
				notify.error(response.error.message || msg("chargebackWebhookCouldNotBeResent"));
				return;
			}

			notify.success(msg("chargebackWebhookResent"));
		} catch (requestError) {
			notify.error(getNotificationErrorMessage(requestError, msg("chargebackWebhookCouldNotBeResent")));
		} finally {
			setResendBusy(false);
		}
	};

	const showRawTab = raw !== undefined || showFileDetails;
	const detailsBody = (
		<VBox gap="22px" sx={{ flex: showRawTab ? "1 0 auto" : "1 1 auto", minHeight: 0, overflowY: showRawTab ? undefined : "auto", padding: "22px 24px 26px", backgroundColor: "#f5f7fa" }}>
			<DetailsCard
				title={msg("cbDetailsMain")}
				aside={
					overviewDate && (
						<Box title={overviewDate.label} sx={{ fontSize: "12px", lineHeight: "16px", color: "#98a2b3", whiteSpace: "nowrap" }}>
							{formatDateTime(overviewDate.value)}
						</Box>
					)
				}>
				{transactionId != null && (
					<DetailsRow label={transactionLabel}>
						<CopyableValue value={transactionId} />
					</DetailsRow>
				)}
				{extraPaymentId != null && (
					<DetailsRow label={msg("paymentId_d9c9afd8")}>
						<CopyableValue value={extraPaymentId} />
					</DetailsRow>
				)}
				{chargeback.decta_payment_id != null && (
					<DetailsRow label={msg("dectaPaymentId")}>
						<CopyableValue value={chargeback.decta_payment_id} />
					</DetailsRow>
				)}
				{chargeback.merchant_id != null && (
					<DetailsRow label={msg("merchant")}>
						{/* Name on the first line, its id under it: a long id must not wrap beside the name. */}
						<VBox sx={{ minWidth: 0 }}>
							<span>{merchantName || msg("templateMerchant_bf3dbf70", { v0: chargeback.merchant_id })}</span>
							{merchantName && (
								<Box sx={{ color: "#98a2b3", fontSize: "12px", lineHeight: "20px" }}>
									<CopyableValue value={chargeback.merchant_id} />
								</Box>
							)}
						</VBox>
					</DetailsRow>
				)}
			</DetailsCard>

			{recordError && <DetailsNotice icon={<AlertCircle size={18} color="#d92d20" />} title={msg("cbLoadFailed")} text={recordError} />}

			{!showFileDetails ? null : fileBusy ? (
				<DetailsSkeleton title={msg("loadingDetailsFromTheSourceFile")} />
			) : !fileData ? (
				<DetailsNotice
					icon={<FileWarning size={18} color="#f79009" />}
					title={msg("noMatchingFileEntry")}
					text={
						<>
							PAYMENT_ID {chargeback.decta_payment_id}
							{msg("wasNotFoundInTheLoadedFile")}
						</>
					}
				/>
			) : (
				<>
					{reason && <ReasonExplainer reason={reason} />}
					{groups.map((group, index) => (
						<Fragment key={group.title}>
							<DetailsCard title={group.title}>
								{group.fields.map(field => (
									<DetailsRow key={field.key} label={displayField(englishText(FIELD_LABELS[field.key] ?? field.key), FIELD_LABELS[field.key] ?? field.key)} mono={field.mono}>
										{fileFieldValue(fileData, field)}
									</DetailsRow>
								))}
							</DetailsCard>
							{index === 0 && session && <SessionDetails session={session} />}
						</Fragment>
					))}
					{groups.length === 0 && session && <SessionDetails session={session} />}

					{comments && (
						<VBox gap="10px">
							<DetailsSectionLabel>{msg("cbDetailsComments")}</DetailsSectionLabel>
							<Box
								sx={{
									padding: "14px 18px",
									backgroundColor: "#fff",
									border: "1px solid #d0d5dd",
									borderRadius: "10px",
									boxShadow: "0 1px 2px rgba(16,24,40,.05)",
									fontSize: "13px",
									lineHeight: "20px",
									color: "#344054",
									whiteSpace: "pre-wrap",
									overflowWrap: "anywhere",
								}}>
								{comments}
							</Box>
						</VBox>
					)}

					{additionalFields.length > 0 && (
						<details style={{ background: "#fff", border: "1px solid #d0d5dd", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,.05)" }}>
							<summary style={{ padding: "12px 18px", cursor: "pointer", fontSize: "13px", lineHeight: "20px", fontWeight: 500, color: "#475467" }}>
								{msg("cbDetailsOtherFields", { v0: additionalFields.length })}
							</summary>
							<Box sx={{ display: "grid", gridTemplateColumns: "180px minmax(0, 1fr)", alignItems: "baseline", gap: "4px 16px", padding: "4px 18px 14px", borderTop: "1px solid #eaecf0" }}>
								{additionalFields.map(([key, value]) => (
									<DetailsRow key={key} label={capitalize(key.replaceAll("_", " ").toLowerCase())}>
										{typeof value === "object" ? JSON.stringify(value) : String(formatFileValue(key, value))}
									</DetailsRow>
								))}
							</Box>
						</details>
					)}
				</>
			)}
		</VBox>
	);

	// Raw tab: the record exactly as it came, for support and for checking fields the cards do not show.
	const rawData = raw ?? fileData;
	const rawText = rawData !== undefined ? JSON.stringify(rawData, null, 2) : undefined;
	const rawBody = (
		<VBox gap="14px" sx={{ flex: "1 0 auto", padding: "20px 24px 24px", backgroundColor: "#f5f7fa" }}>
			{fileBusy && rawText === undefined ? (
				<Box sx={{ padding: "18px", backgroundColor: "#fff", border: "1px solid #d0d5dd", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,.05)" }}>
					<FileContentSkeleton />
				</Box>
			) : rawText ? (
				<Box sx={{ minWidth: 0, fontSize: "12px", lineHeight: "18px" }}>
					<Pre data={rawText} wrap />
				</Box>
			) : (
				<Typo sx={{ fontSize: "13px", color: "#667085" }}>{msg("noFileContentAvailable")}</Typo>
			)}
		</VBox>
	);

	return (
		<Panel
			growContent
			disableScroll
			decorator="absolute"
			onClose={onClose}
			sx={{ padding: "0", width: "720px", maxWidth: "94vw", maxHeight: "88dvh", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
			<VBox sx={{ flex: 1, minHeight: 0, fontFamily: "'Euclid Circular B', sans-serif" }}>
				<VBox gap="10px" sx={{ flex: "0 0 auto", padding: showRawTab ? "16px 20px 8px 24px" : "16px 20px 16px 24px", borderBottom: showRawTab ? "none" : "1px solid #edf0f5", backgroundColor: "#fff" }}>
					<HBox sx={{ alignItems: "flex-start", gap: "12px" }}>
						<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
							<Typo sx={{ fontSize: "11px", lineHeight: "16px", color: "#667085" }}>{msg("chargeback")}</Typo>
							<Typo sx={{ fontSize: "21px", lineHeight: "28px", fontWeight: 600, color: "#182230" }}>
								{cents(chargeback.amount)} {chargeback.currency || "EUR"}
							</Typo>
						</VBox>
						<Box
							onClick={onClose}
							title={msg("cbClose")}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								width: "32px",
								height: "32px",
								borderRadius: "7px",
								color: "#667085",
								cursor: "pointer",
								"&:hover": { backgroundColor: "#f2f4f7", color: "#182230" },
							}}>
							<X size={18} />
						</Box>
					</HBox>

					{/* State and reason on the left; actions on the same line, right-aligned. */}
					<HBox sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
						{chargeback.status && tone && (
							<Box
								sx={{
									padding: "2px 8px",
									borderRadius: "6px",
									border: `1px solid ${tone.border}`,
									backgroundColor: tone.background,
									color: tone.color,
									fontSize: "12px",
									lineHeight: "18px",
									fontWeight: 500,
								}}>
								{chargebackStatusLabel(chargeback.status)}
							</Box>
						)}
						{reasonCode && (
							<HBox
								title={[cardBrand && BRAND_NAMES[cardBrand], `${msg("reasonCode")} ${reasonCode}`, reason?.category.label()].filter(Boolean).join(" · ")}
								sx={{
									alignItems: "center",
									gap: "6px",
									minWidth: 0,
									padding: "2px 8px",
									borderRadius: "6px",
									border: `1px solid ${(reason?.category.tone ?? STATUS_TONES.gray).border}`,
									backgroundColor: (reason?.category.tone ?? STATUS_TONES.gray).background,
									color: (reason?.category.tone ?? STATUS_TONES.gray).color,
									fontSize: "12px",
									lineHeight: "18px",
								}}>
								{cardBrand && <CardBrandMark brand={cardBrand} height={BRAND_MARK_HEIGHT[cardBrand].header} />}
								{reason ? (
									<>
										<span style={{ fontWeight: 600 }}>{reasonCode}</span>
										<span>{reason.title()}</span>
									</>
								) : (
									`${msg("reasonCode")} ${reasonCode}`
								)}
							</HBox>
						)}
						<Spacer grow />
						{showResend && (
							<Button
								variant="secondarySmall"
								icon={<RefreshCw size={15} />}
								title={resendBusy ? msg("resending") : msg("resendWebhook")}
								disabled={resendBusy}
								onClick={resendWebhook}
							/>
						)}
						<Button
							variant="secondarySmall"
							icon={<ExternalLink size={15} />}
							title={msg("openTransaction")}
							disabled={transactionId == null && !onOpenTransaction}
							onClick={() => {
								if (onOpenTransaction) onOpenTransaction();
								else if (transactionId != null) S_DECTA_SESSION_POPUP.invoke({ sessionId: String(transactionId) });
							}}
						/>
					</HBox>
				</VBox>

				{showRawTab ? (
					<VBox sx={{ flex: 1, minHeight: 0 }}>
						<Tabs variant="modern" scrollable spaceBottom={0} sx={{ padding: "0 24px", backgroundColor: "#fff" }}>
							<TabItem title={msg("cbDetailsTab")} section={0} icon={<List size={16} />}>
								{detailsBody}
							</TabItem>
							<TabItem title="Raw" section={1} icon={<FileJson size={16} />}>
								{rawBody}
							</TabItem>
						</Tabs>
					</VBox>
				) : (
					detailsBody
				)}
			</VBox>
		</Panel>
	);
};

const formatUtcDateParts = (year: number, month: number, day: number) =>
	new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(isRussian() ? "ru-RU" : undefined, { timeZone: "UTC" });

const formatDateTime = (value: string) => formatDisplayDateTime(value, { separator: " - ", fallback: value });

const formatFileValue = (key: string, value: unknown) => {
	if (typeof value !== "string" || !key.toUpperCase().includes("DATE")) return value;

	const dectaDate = value.match(/^(\d{4})\.(\d{2})\.(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/);
	if (dectaDate) {
		const [, year, month, day, hours = "00", minutes = "00", seconds = "00"] = dectaDate;
		const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds)));
		return dectaDate[4]
			? formatDisplayDateTime(date, { separator: " - " })
			: formatDisplayDate(date);
	}

	return formatDateTime(value);
};

export const CopyableValue = ({ value, bold }: { value: string | number; bold?: boolean }) => { useLocale(); return ((
	<HBox
		title={msg("copy")}
		onClick={event => {
			event?.stopPropagation();
			navigator.clipboard.writeText(String(value));
			notify.copied();
		}}
		sx={{
			alignItems: "center",
			gap: "5px",
			width: "max-content",
			maxWidth: "100%",
			padding: "2px 5px",
			margin: "-2px -5px",
			borderRadius: "4px",
			cursor: "copy",
			fontWeight: bold ? 600 : undefined,
			transition: "background-color .15s",
			"&:hover": { backgroundColor: "rgba(0,0,0,.06)" },
		}}>
		<Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Box>
		<Clipboard size={14} opacity={0.55} />
	</HBox>
)); };

